import React, { useMemo, useEffect, useState, useRef } from "react";
import { AppDispatch, RootState } from "../Redux/store";
import { useDispatch, useSelector } from "react-redux";
import { useAuth0 } from "@auth0/auth0-react";
import { X } from "lucide-react";
import { ChevronDown } from "lucide-react";
import { FormControl } from "@mui/material";
import axios from "axios";
import { GetcommunicationDetail } from "../Redux/communicationDetailSlice";
import { fetchAllMatters } from "../Redux/matterSlice";
import { API_BASE_URL, CreateCommunicationsform, GetStatus,  GraphMicrosoftBaseURL, Attachments, value, UploadURL, IngnoreFileExtensions } from "../../config/config";
import { fetchAllContacts } from "../Redux/contactSlice";
import { debug } from "webpack";
import { Co2Sharp, DatasetSharp, Description, Subject } from "@mui/icons-material";
import Status from "./Status";
import { GetLogStatus } from "../Redux/logStatus";
import { GetTimeSlipSetting } from "../Redux/timeSlipSettingsSlice";
import { getProfileByEmail, getTokenByProfileEmail, getStorageItem, SetStorageItem } from "../../helpers/helpers";
import { extractTenantIdFromToken } from "../../helpers/helpers";
import Loader from "../MessageAlert/Loader";
import { Box, CircularProgress } from "@mui/material";
import { json } from "react-router-dom";
import {DEFAULT_UI_DICTIONARY,LOCALSTORAGE_KEYS } from '../../utils/Constants';
import { getAccessToken as getManagedAccessToken } from '../../services/tokenManager';
import { useCappedOptions, LoadAllFooter } from './dropdownOptions';

/**
 * Searchable single/multi select.
 *
 * IMPORTANT: this MUST stay at module scope. It previously lived inside
 * EmailLoggingForm, which meant a brand-new component *type* was created on
 * every parent render - React cannot reconcile that against the previous
 * type, so it unmounted and remounted the whole dropdown each time. The
 * `isOpen` / `search` state below was therefore reset constantly: the list
 * snapped shut and whatever the user had typed was wiped mid-search. That was
 * the "flickering while searching" report. Paging made it obvious (each of
 * the ~20-40 sequential page fetches re-rendered the form) but ANY parent
 * re-render triggered it. LogTimeslipForm.tsx / LogClarraDocsForm.tsx already
 * declare their copies of this component at module scope.
 */
const Dropdown = React.memo(({
    label,
    value,
    options,
    onChange,
    isMulti,
    required,
    disabled = false,
    isLoading = false,
    loadingLabel = "Loading...",
}: any) => {
    const [isOpen, setIsOpen] = useState(false);
    const [search, setSearch] = useState("");
    const dropdownRef = React.useRef<HTMLDivElement>(null);

    // While the underlying list is still paging in, the control accepts no
    // typing or selection - picking from a half-loaded list is how users ended
    // up "confused when they search". The spinner + count tell them why.
    const locked = disabled || isLoading;

    // Filtering, the MAX_VISIBLE_OPTIONS render cap and the "Load all" escape
    // hatch all live in ./dropdownOptions, shared with LogTimeslipForm and
    // LogClarraDocsForm so the three copies cannot drift apart again.
    const {
        filteredOptions,
        visibleOptions,
        hiddenCount,
        showAllOptions,
        isExpanding,
        loadAll,
    } = useCappedOptions(options, search, isOpen);

    // A list that finishes loading while open should drop the stale search.
    useEffect(() => {
        if (isLoading) {
            setIsOpen(false);
            setSearch("");
        }
    }, [isLoading]);

    useEffect(() => {
        const handleOutsideClick = (event: MouseEvent) => {
            if (
                dropdownRef.current &&
                !dropdownRef.current.contains(event.target as Node)
            ) {
                setIsOpen(false);
            }
        };

        document.addEventListener("mousedown", handleOutsideClick);

        return () => {
            document.removeEventListener("mousedown", handleOutsideClick);
        };
    }, []);
    return (
        <div className="mb-0" ref={dropdownRef}>
            <label className="block">
                {label}
                {required && <span className="text-red-500">*</span>}
            </label>
            <div className="relative">
                <div
                    className={`border rounded-lg p-2 min-h-[10px] ${locked ? 'bg-gray-100 cursor-not-allowed' : 'cursor-text bg-white'
                        }`}
                    onClick={() => !locked && setIsOpen(true)}
                >
                    {!locked && (
                        <ChevronDown
                            className={`absolute right-2 top-3 text-gray-400 transition-transform ${isOpen ? "transform rotate-180" : ""
                                }`}
                            size={12}
                        />
                    )}
                    {isLoading && (
                        <span className="absolute right-2 top-2 flex items-center">
                            <CircularProgress size={12} />
                        </span>
                    )}
                    {isMulti ? (
                        <div className="flex flex-wrap gap-1">
                            {value.map((v: any, index: number) => (
                                <span
                                    // key={v.value}
                                    key={index}
                                    className="inline-flex items-center bg-blue-50 rounded-md px-2 m-1"
                                >
                                    <span
                                        className="text-sm text-gray-700"
                                        title={v.label}
                                        style={{
                                            maxWidth: 200,
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            display: "inline-block",
                                            verticalAlign: "bottom",
                                        }}
                                    >
                                        {v.label}
                                    </span>
                                    {!disabled && !v.native && (
                                        <button
                                            onClick={() =>
                                                onChange(
                                                    value.filter((item: any) => item.value !== v.value)
                                                )
                                            }
                                            className="ml-1 text-gray-400 hover:text-gray-600 focus:outline-none"
                                        >
                                            <X size={12} />
                                        </button>
                                    )}
                                </span>
                            ))}
                            {/* Any already-selected chips above stay visible while
                                loading - only the search input is swapped for the
                                loading hint, so the user never loses sight of the
                                recipients already on the message. */}
                            {!disabled && (isLoading ? (
                                <span className="text-sm text-gray-500 italic py-1">{loadingLabel}</span>
                            ) : (
                                <input
                                    type="text"
                                    className="outline-none flex-grow min-w-[100px] text-sm"
                                    placeholder="Type to search..."
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    onFocus={() => setIsOpen(true)}
                                />
                            ))}
                            {disabled && value.length === 0 && (
                                <span className="text-sm text-gray-500">Not Available</span>
                            )}
                        </div>
                    ) : (
                        <div className="flex items-center">
                            {value ? (
                                <>
                                    <span className="text-sm">{value.label}</span>
                                    {!disabled && (
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                onChange(null);
                                            }}
                                            className="ml-2 text-gray-400 hover:text-gray-600"
                                        >
                                            <X size={12} />
                                        </button>
                                    )}
                                </>
                            ) : (
                                <>
                                    {disabled ? (
                                        <span className="text-sm text-gray-500">Select an option...</span>
                                    ) : isLoading ? (
                                        <span className="text-sm text-gray-500 italic">{loadingLabel}</span>
                                    ) : (
                                        <input
                                            type="text"
                                            className="outline-none w-full text-sm"
                                            placeholder="Type to search..."
                                            value={search}
                                            onChange={(e) => setSearch(e.target.value)}
                                            onFocus={() => setIsOpen(true)}
                                        />
                                    )}
                                </>
                            )}
                        </div>
                    )}
                </div>

                {isOpen && !locked && (
                    <div className="absolute z-10 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-60 overflow-auto">
                        {visibleOptions.map((option) => (
                            <div
                                key={option.value}
                                className="px-4 py-2 hover:bg-gray-100 cursor-pointer"
                                onClick={() => {
                                    if (isMulti) {
                                        if (!value.find((item) => item.value === option.value)) {
                                            onChange([...value, option]);
                                        }
                                    } else {
                                        onChange(option);
                                    }
                                    setSearch("");
                                    setIsOpen(false);
                                }}
                            >
                                {option.label}
                            </div>
                        ))}
                        <LoadAllFooter
                            filteredCount={filteredOptions.length}
                            hiddenCount={hiddenCount}
                            showAllOptions={showAllOptions}
                            isExpanding={isExpanding}
                            onLoadAll={loadAll}
                        />
                        {filteredOptions.length === 0 && (
                            <div className="px-4 py-2 text-xs text-gray-500">No matches</div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
});
Dropdown.displayName = "Dropdown";

const EmailLoggingForm = ({
    onSubmit,
    formData,
    setFormData,
    SetEmailItem,
    Reset,
    expanded,
    errors,
    setSelectedTo,
    selectedTo,
    selectedCc,
    setSelectedCc,
    selectedFrom,
    setSelectedFrom,
    setErrors,
    IsLogged,
    hideView,
    getAllData,
}: {
    setOnlyTimeSlip: any;
    createTimeslip: any;
    setOnlyDocs: any;
    expanded: any;
    setErrors: any;
    errors: any;
    setSelectedFrom: any;
    selectedFrom: any;
    selectedTo: any;
    setSelectedTo: any;
    selectedCc: any,
    setSelectedCc: any;
    createClarraDocs: any;
    setCommunicationSave: any;
    onSubmit: any;
    formData: any;
    setFormData: any;
    SetEmailItem: any;
    Reset: any;
    IsLogged: any;
    hideView: any;
    getAllData: any;
}) => {
    SetStorageItem(LOCALSTORAGE_KEYS.apibaseurl, API_BASE_URL);
    SetStorageItem(LOCALSTORAGE_KEYS.UploadURL, UploadURL);
    SetStorageItem(LOCALSTORAGE_KEYS.IngnoreFileExtensions, IngnoreFileExtensions);
    SetStorageItem("CreateCommunicationsform", CreateCommunicationsform);
    const tenantId = extractTenantIdFromToken();
    const dispatch = useDispatch<AppDispatch>();
    const matters = useSelector((state: RootState) => state.matters.matters);
    const mattersPageCount = useSelector((state: RootState) => state.matters.pageCount);
    const mattersNextPage = useSelector((state: RootState) => state.matters.page);
    const tags = useSelector((state: RootState) => state.tags.tags);
    const contacts = useSelector((state: RootState) => state.contact.contacts);
    const contactsPageCount = useSelector((state: RootState) => state.contact.pageCount);
    const contactsNextPage = useSelector((state: RootState) => state.contact.page);

    // "Still loading" for the dropdowns means the WHOLE paged run is not done
    // yet, not just the single request in flight. The slices' own `loading`
    // flag drops to false between every page, so on its own it would strobe
    // ~20-40 times during a large load. `page < pageCount` stays true for the
    // entire run, which is what the user needs to see.
    const mattersFetching = useSelector((state: RootState) => state.matters.loading);
    const contactsFetching = useSelector((state: RootState) => state.contact.loading);
    const mattersLoading = mattersFetching || mattersNextPage < mattersPageCount;
    const contactsLoading = contactsFetching || contactsNextPage < contactsPageCount;
    const communicationDetail = useSelector(
        (state: RootState) => state.communicationDetail.communicationDetail
    );
    const logStatus = useSelector((state: RootState) => state.logStatus.logStatus)
    const Detailloading = useSelector(
        (state: RootState) => state.communicationDetail.loading
    );

    


    const [isCcDisabled, setIsCcDisabled] = useState(false);
    const [resetFlag, setResetFlag] = useState(0);
    const [isClearing, setIsClearing] = useState(false);
    const [officeItemReady, setOfficeItemReady] = useState(false);
    const { uiDictionary} = useSelector((state: RootState) => state.labels);

    // let emailOptions = contacts
    //   .flatMap((item) => item.contactEmails)
    //   .filter((emailObj) => emailObj && emailObj.email)
    //   .map((emailObj) => ({ value: item.id + "|" + item.userId , label: emailObj.email }));

    // let emailOptions = contacts
    //     .flatMap((item) =>
    //         item.contactEmails.map((emailObj) => ({
    //             id: item.id,
    //             userId: item.userId,
    //             firstName: item.firstName,
    //             lastName: item.lastName,
    //             emailObj: emailObj
    //         }))
    //     )
    //     .filter((entry) => entry.emailObj && entry.emailObj.email)
    //     // .map((entry) => ({
    //     //     value: entry.id + "|" + entry.userId,
    //     //     label: ((entry.companyName) ? entry.companyName : entry.firstName + " " + entry.lastName) + " <" + entry.emailObj.email + ">"
    //     // }));
    //         .map((entry) => ({
    //         value: entry.id + "|" + entry.userId + "|" + entry.emailObj.email,
    //         label: ((entry.companyName) ? entry.companyName : entry.firstName + " " + entry.lastName) + " <" + entry.emailObj.email + ">"
    //     }));

    // New Code - 15-07-25//
    const emailOptions = useMemo(() =>
        contacts
            .flatMap((item) =>
                item.contactEmails.map((emailObj) => ({
                    id: item.id,
                    userId: item.userId,
                    firstName: item.firstName,
                    lastName: item.lastName,
                    emailObj: emailObj
                }))
            )
            .filter((entry) => entry.emailObj && entry.emailObj.email)
            .map((entry) => ({
                value: entry.id + "|" + entry.userId + "|" + entry.emailObj.email,
                label: ((entry.companyName) ? entry.companyName : entry.firstName + " " + entry.lastName) + " <" + entry.emailObj.email + ">"
            }))
        , [contacts]);

    // END CODE//

    // let Contacts = contacts
    //   .flatMap((item) => item.contactEmails)
    //   .filter((emailObj) => emailObj && emailObj.email)
    //   .map((emailObj) => ({ value: emailObj.email, label: item.firstName + " " + item.lastName  }));

    const [mailItem, setMailItem] = useState({
        subject: "",
        body: "",
        attachments: null,
        sender: "",
        senderDisplayName: "",
        recipents: [],
        cc: [],
        date: null,
        time: "",
        timezone: "",
        referenceid: "",
        conversationid: "",
        internetMessageId :"",
    });
    const [emailbody, setMailBody] = useState("");
    const [IsReset, SetIsReset] = useState(true);

    const [files, SetFiles] = useState([]);
    const [CreateDate, SetCreateDate] = useState("");
    const { logout, getAccessTokenSilently } = useAuth0();
    const [isFetchAllMatter, setIsFetchAllMatter] = useState(false)
    const [lastItemId, SetlastItemId] = useState("");
    const [PreviousItemId, SetPreviousItemId] = useState("");

    // Helper: Get email body
    // function getBody(item) {
    //   return new Promise((resolve, reject) => {
    //     item.body.getAsync(Office.CoercionType.Text, (result) => {
    //       if (result.status === Office.AsyncResultStatus.Succeeded) {
    //         resolve(result.value);
    //       } else {
    //         reject(result.error);
    //       }
    //     });
    //   });
    // }

    // Helper: Send data to server
    async function sendToServer(emailData) {
        // console.log(emailData);
    }

    // const handleAttachmentsCallback = (result) => {
    //   try {

    //    // console.log("handleAttachmentsCallback-01:-", result);
    //     // Identifies whether the attachment is a Base64-encoded string, .eml file, .icalendar file, or a URL.
    //    // console.log("handleAttachmentsCallback-02:-", result.value);
    //     switch (result.value.format) {
    //       case Office.MailboxEnums.AttachmentContentFormat.Base64:
    //         // Handle file attachment.
    //      //   console.log("Attachment is a Base64-encoded string.");
    //         break;
    //       case Office.MailboxEnums.AttachmentContentFormat.Eml:
    //         // Handle email item attachment.
    //        // console.log("Attachment is a message.");
    //         break;
    //       case Office.MailboxEnums.AttachmentContentFormat.ICalendar:
    //         // Handle .icalender attachment.
    //        // console.log("Attachment is a calendar item.");
    //         break;
    //       case Office.MailboxEnums.AttachmentContentFormat.Url:
    //         // Handle cloud attachment.
    //        // console.log("Attachment is a cloud attachment.");
    //         break;
    //       default:
    //       // Handle attachment formats that aren't supported.
    //     }
    //   } catch (error) { }
    // };

    // function base64ToBlob(base64, mimeType) {
    //   // Decode the Base64 string
    //   const byteCharacters = atob(base64);
    //   const byteNumbers = new Array(byteCharacters.length)
    //     .fill(0)
    //     .map((_, i) => byteCharacters.charCodeAt(i));
    //   const byteArray = new Uint8Array(byteNumbers);

    //   // Create a Blob with the specified MIME type
    //   return new Blob([byteArray], { type: mimeType });
    // }

    function base64ToFile(base64, fileName, mimeType) {
        //const blob = base64ToBlob(base64, mimeType);
        const blob = new Blob(
            [
                new Uint8Array(
                    atob(base64)
                        .split("")
                        .map((char) => char.charCodeAt(0))
                ),
            ],
            { type: "application/octet-stream" }
        );
        return blob;
    }

    // Commented on 2nd Feb
    function detectAddinContext() {
        if (window.innerWidth > 0) {
        }
        else {
        }
    }

    const valueForEmail = (email, emailOptions) => {
        const target = (email || '').trim().toLowerCase();
        const found = emailOptions.find(opt => {
            const m = typeof opt.label === 'string' ? opt.label.match(/<([^>]+)>/) : null;
            const lblEmail = m ? m[1].trim().toLowerCase() : '';
            if (lblEmail) return lblEmail === target;
            const parts = (opt.value || '').split('|');
            const valEmail = (parts[2] || '').trim().toLowerCase();
            return valEmail === target;
        });
        return found?.value ?? `0||${email}`;
    };

    const clearFormData = () => {
        setSelectedMatter(null);
        setSelectedTags([]);
        setHasAttachment(false);
        setSelectedTo([]);
        setSelectedCc([]);
        
        localStorage.removeItem(LOCALSTORAGE_KEYS.Attachments);
        localStorage.removeItem(LOCALSTORAGE_KEYS.ItemDate)
        localStorage.removeItem(LOCALSTORAGE_KEYS.SelectedCC);
        localStorage.removeItem(LOCALSTORAGE_KEYS.SelectedTO);
        localStorage.removeItem(LOCALSTORAGE_KEYS.SelectedMatter);
        localStorage.removeItem(LOCALSTORAGE_KEYS.SelectedTags);

        // localStorage.removeItem("ItemDate");
        // localStorage.removeItem("SelectedCc");
        // localStorage.removeItem("SelectedTo");
        // localStorage.removeItem("SelectedMatter");
        // localStorage.removeItem("SelectedTags");
        
        setFormData((prev: any) =>
        ({
            ...prev,
            isValid: false,
            // profileEmail: null
            billable: true,
            date: formatDate(new Date()),
            duration: "",
            timekeeper: "",
            matter: "",
            matterid:0,
            tag: [],
            activityCode: "",
            taskCode: "",
            subject: "",
            description: "",
            note: "",
            data: "",
            status: "",
            serviceType: null,
            attachments: [],
            emlattachment: {
                blobfile: null,
                filename: "",
            },
            sendToDocketing: false,
            docketingComplete: false,
            docketingNotes: "",
            Data: "",
            createClarraDocs: false,
            createCommunication: true,
            createTimeslip: false,
            mailItem: {},
            mailbody: "",
            matterdoc: "",
            matteriddoc:0,
            matterdoc_manually_set: false,
            tagdoc: [],
            datedoc: formatDate(new Date()),
            subjectdoc: "",
            descriptiondoc: "",
            attachmentsdoc: [],
            emlFileName: "",
            datets: formatDate(new Date()),
            matterts: "",
            matteridts:0,
            matterts_manually_set: false,
            notedoc: "",
            isClarraDocsReadOnly: false,
            isCEmailLogged: false,
            isTimeSlipLogged: false,
            isClarraDocsLogged: false,
            IsComposedMode: true,
            IsComposedTextShown: false,
            CommunicationId: 0,
            TimeSlipId: 0,
            ClarraDocsId: 0,
            AllLogged: false,
            ZapierDownloadURL:[]
        }));
    };
    const getAttachmentWithRetry = (item, attachment, retries = 2) => {
    return new Promise((resolve, reject) => {
        const attempt = (retryCount) => {
            item.getAttachmentContentAsync(attachment.id, (result) => {

                if (
                    result.status === Office.AsyncResultStatus.Succeeded &&
                    result.value &&
                    result.value.content
                ) {
                    return resolve(result); // ✅ only resolve valid result
                }

                if (retryCount > 0) {
                    return setTimeout(() => attempt(retryCount - 1), 300);
                }

                reject(result.error || new Error("Failed to get attachment content"));
            });
        };

        attempt(retries);
    });
};
    let activeAttachmentRequestId = 0;
    const ReadEmailItem = async (mode, item: any) => {
        setIsEmailRead(true);
        console.log("ReadEmailItem - Mode ItemId", mode, item.itemId);
        console.log("ReadEmailItem - Current email internetMessageId:", item.internetMessageId);
        let token = await getAccessToken();
        let EmailItemid = "";
        Office.onReady((info) => {
            try {
                localStorage.removeItem(LOCALSTORAGE_KEYS.EmailAPIObject);
                window.addEventListener("beforeunload", () => {
                    SetStorageItem(LOCALSTORAGE_KEYS.mode, "0");
                    // Save state or cleanup here
                });
                if (Office.addin && Office.addin.onVisibilityModeChanged) {
                    Office.addin.onVisibilityModeChanged((args) => {
                        if (args.visibilityMode === Office.VisibilityMode.hidden) {
                            // console.log("Task Pane is closing...");
                            SetStorageItem(LOCALSTORAGE_KEYS.mode, "0"); // Save state
                        }
                    });
                }
               
                if (
                    info.host === Office.HostType.Outlook ||
                    info.platform === Office.PlatformType.OfficeOnline
                ) {
                    detectAddinContext();
                   
                    const userProfilr = Office.context.mailbox.userProfile;
                    const profileEmail = userProfilr.emailAddress
                    const doseExistProfile = getProfileByEmail(profileEmail)

                    if (doseExistProfile) {
                        const doseExistToken = getTokenByProfileEmail(profileEmail)
                        if (doseExistToken) {

                            if (mode == 0) {
                                //console.log('Loading Matter');
                                getAllMatter()

                            }
                        }
                    } else {
                        // setHasKeyAccess(false)
                    }
                    if (mode == 0) {
                        setFormData((prev: any) => ({
                            ...prev,
                            profileEmail: userProfilr.emailAddress
                        }));
                    }


                    if (item.itemId != null) {
                        EmailItemid = item.itemId;
                        setCompodeMode(false);
                        setFormData((prev: any) => ({
                            ...prev,
                            IsComposedMode: false
                        }));
                       
                        setFormData((prev: any) => ({
                            ...prev,
                            IsComposedTextShown: false
                        }));
                    }
                    else {
                        setCompodeMode(true);
                        SetCreateDate(formatDate(new Date()));
                        setFormData((prev: any) => ({
                            ...prev,
                            IsComposedMode: true
                        }));
                        
                        setFormData((prev: any) => ({
                            ...prev,
                            IsComposedTextShown: true
                        }));
                        
                    }
                   
                    const attachments = item.attachments
                        ? item.attachments.filter((att) => !att.isInline)
                        : [];

                    setHasAttachment(true);
                    
                    if (attachments && attachments.length > 0) {
                        setHasAttachment(true);
                        SetEmailItem(item);
                        const options = { asyncContext: { currentItem: item } };

                        const fileAttachPromises = attachments.map(async (attachment) => {
                            const result = await getAttachmentWithRetry(item, attachment) as Office.AsyncResult<Office.AttachmentContent>;
                            console.log("file");
                            console.log(result);
                            //const { content } = result["value"];
                            const { content, format } = result.value;
                            const Formats = Office.MailboxEnums.AttachmentContentFormat;

                            let blobfile: Blob | null = null;
                            let filename = attachment.name;

                            switch (format) {
                                case Formats.Base64: {
                                    // Normal file attachment
                                    const byteArray = Uint8Array.from(atob(content), c => c.charCodeAt(0));
                                    blobfile = new Blob([byteArray]);
                                    break;
                                }
                                case Formats.Eml: {
                                    // Outlook item attachment (.msg / embedded email) -> raw MIME string
                                    blobfile = new Blob([content], { type: "message/rfc822" });
                                    if (!/\.eml$/i.test(filename || "")) {
                                        filename = (filename || "attached-email") + ".eml";
                                    }
                                    break;
                                }
                                case Formats.ICalendar: {
                                    // Calendar invite (.ics) -> raw iCalendar string
                                    blobfile = new Blob([content], { type: "text/calendar" });
                                    if (!/\.ics$/i.test(filename || "")) {
                                        filename = (filename || "calendar-event") + ".ics";
                                    }
                                    break;
                                }
                                case Formats.Url:
                                default:
                                    // Cloud (OneDrive/SharePoint) attachment is just a link, not bytes,
                                    // and unknown formats can't be uploaded. Skip without failing.
                                    console.warn("Skipping attachment (cloud/unsupported format):", format, attachment.name);
                                    return null;
                            }

                            if (!blobfile || blobfile.size === 0) {
                                throw new Error(`Empty blob for ${attachment.name}`);
                            }

                            return {
                                blobfile,
                                filename,
                            };
                            // return new Promise((resolve, reject) => {
                            //     //console.log(attachment.id);
                            //     item.getAttachmentContentAsync(attachment.id, (result) => {
                            //         console.log("attachment id",attachment.id);
                            //         if (result.status === Office.AsyncResultStatus.Succeeded) {
                            //             const { content, format } = result.value;
                            //             if (format === Office.MailboxEnums.AttachmentContentFormat.Base64) {
                            //                 try {
                            //                     // Convert Base64 to Blob or File
                            //                     let file = base64ToFile(content, attachment.name, null);
                            //                     resolve({
                            //                         blobfile: file,
                            //                         filename: attachment.name,
                            //                     });
                            //                 } catch (error) {
                            //                     reject(`Error converting Base64 to file: ${error}`);
                            //                 }
                            //             } else {
                            //                 reject(`Unsupported format: ${format}`);
                            //             }
                            //         } else {
                            //             reject(`Failed to get ${uiDictionary.Attachment} content: ${result.error.message}`);
                            //         }
                            //     });
                            // });
                        });
                        // Wait for all attachments to be processed

                        Promise.all(fileAttachPromises)
                            .then((allFiles) => {
                                // Drop skipped attachments (nulls, e.g. cloud links).
                                const files = allFiles.filter(Boolean);
                                SetFiles(files);
                                setFormData((prev: any) => ({
                                    ...prev,
                                    attachments: files,
                                }));
                                setFormData((prev: any) => ({
                                    ...prev,
                                    attachmentsdoc: files,
                                }));
                                if (getStorageItem(LOCALSTORAGE_KEYS.Attachments) != null)
                                    localStorage.removeItem(LOCALSTORAGE_KEYS.Attachments);
                                SetStorageItem(
                                    LOCALSTORAGE_KEYS.Attachments,
                                    JSON.stringify(files)
                                );
                                // Do something with the files array
                            })
                            .catch((error) => {
                                console.error(
                                    `Failed to fetch attachments `,
                                    error
                                );
                            });

                    }

            

                    const EmaildateTime = (item.dateTimeCreated) ? item.dateTimeCreated : new Date();
                    SetStorageItem(LOCALSTORAGE_KEYS.ItemDate, EmaildateTime.toString());
                    SetCreateDate(formatDate(EmaildateTime));
                    const hours = (EmaildateTime) ? EmaildateTime.getHours() : "00";    // Hours (0-23)
                    const minutes = (EmaildateTime) ? EmaildateTime.getMinutes() : "00"; // Minutes (0-59)
                    const seconds = (EmaildateTime) ? EmaildateTime.getSeconds() : "00"; // Seconds (0-59)
                    //const date = currentDateTime;
                    const time = `${hours}:${minutes}:${seconds}`;

                    // MARKER: START-DESKTOP-TO-CC-SYNC-FIX
                    // For Desktop Outlook compose, use async API to read TO/CC recipients
                    // Web Outlook works with sync reads, but Desktop requires getAsync()
                    const getRecipients = async (): Promise<{ to: any[]; cc: any[] }> => {
                        return new Promise((resolve: (value: { to: any[]; cc: any[] }) => void) => {
                            const recipients = { to: [], cc: [] };
                            const itemTyped = item as any; // Type-cast to access properties

                            // Try to get TO recipients
                            if (itemTyped.to && typeof itemTyped.to.getAsync === 'function') {
                                console.log("[EmailLog] Using async getAsync() for TO recipients (Desktop)");
                                itemTyped.to.getAsync((result: any) => {
                                    if (result.status === Office.AsyncResultStatus.Succeeded && result.value) {
                                        recipients.to = result.value;
                                        console.log("[EmailLog] TO recipients retrieved:", recipients.to);
                                    } else {
                                        console.log("[EmailLog] TO getAsync failed, falling back to sync");
                                    }
                                    // Try to get CC recipients
                                    if (itemTyped.cc && typeof itemTyped.cc.getAsync === 'function') {
                                        console.log("[EmailLog] Using async getAsync() for CC recipients (Desktop)");
                                        itemTyped.cc.getAsync((ccResult: any) => {
                                            if (ccResult.status === Office.AsyncResultStatus.Succeeded && ccResult.value) {
                                                recipients.cc = ccResult.value;
                                                console.log("[EmailLog] CC recipients retrieved:", recipients.cc);
                                            } else {
                                                console.log("[EmailLog] CC getAsync failed, using fallback");
                                            }
                                            resolve(recipients);
                                        });
                                    } else {
                                        recipients.cc = (itemTyped.cc) ? itemTyped.cc : [];
                                        console.log("[EmailLog] CC using sync read");
                                        resolve(recipients);
                                    }
                                });
                            } else {
                                // Fallback to sync reads (Web Outlook path)
                                recipients.to = (itemTyped.to) ? itemTyped.to : [];
                                recipients.cc = (itemTyped.cc) ? itemTyped.cc : [];
                                console.log("[EmailLog] Using sync reads for recipients (Web Outlook)");
                                resolve(recipients);
                            }
                        });
                    };

                    getRecipients().then((recipients) => {
                        const itemTyped4 = item as any;
                        console.log("[EmailLog] Final recipients - TO:", recipients.to.length, "CC:", recipients.cc.length);
                        setMailItem({
                            ...mailItem,
                            attachments:
                                attachments && attachments.length > 0 ? attachments : [],
                            subject: itemTyped4.subject,
                            sender: (itemTyped4.sender && itemTyped4.sender.emailAddress) ? itemTyped4.sender.emailAddress : userProfilr.emailAddress,
                            senderDisplayName: (itemTyped4.sender) ? itemTyped4.sender.displayName : userProfilr.displayName,
                            recipents: recipients.to,
                            cc: recipients.cc,
                            date: (itemTyped4.dateTimeCreated) ? itemTyped4.dateTimeCreated : new Date(),
                            time: time,
                            referenceid: itemTyped4.itemId,
                            conversationid: itemTyped4.conversationId,
                            internetMessageId: itemTyped4.internetMessageId,
                            timezone: userProfilr.timeZone
                        });
                    });
                    // MARKER: END-DESKTOP-TO-CC-SYNC-FIX

                    // Use async recipients from Desktop Outlook fix above (fallback to sync if async failed)
                    const itemTyped2 = item as any;
                    var mitem = {
                        body: "",
                        attachments:
                            attachments && attachments.length > 0 ? attachments : [],
                        subject: item.subject,
                        sender: (item.sender && item.sender.emailAddress) ? item.sender.emailAddress : userProfilr.emailAddress,
                        senderDisplayName: (item.sender) ? item.sender.displayName : userProfilr.displayName,
                        recipents: (itemTyped2.to) ? itemTyped2.to : [],
                        cc: (itemTyped2.cc) ? itemTyped2.cc : [],
                        date: (item.dateTimeCreated) ? item.dateTimeCreated : new Date(),
                        time: time,
                        referenceid: item.itemId,
                        conversationid: item.conversationId,
                        internetMessageId :item.internetMessageId,
                        timezone: userProfilr.timeZone
                    };

                    setFormData((prev: any) => ({
                        ...prev,
                        emlFileName: (item.itemId) ? ((item.subject.length > 50 ? item.subject.substring(0, 47) : item.subject) + ".eml") : ".eml file will attached when Send"
                    }));
                    setFormData((prev: any) => ({
                        ...prev,
                        description: (item.itemId) ? item.subject : ""
                    }));
                    setFormData((prev: any) => ({
                        ...prev,
                        descriptiondoc: (item.itemId) ? item.subject : ""
                    }));
                   

                    item.body.getAsync(Office.CoercionType.Html, (result) => {
                        if (result.status === Office.AsyncResultStatus.Succeeded) {
                            setMailBody(result.value);

                            //const encoded =   btoa(unescape(encodeURIComponent(result.value)));
                            const encoded = btoa(unescape(encodeURIComponent(result.value)));
                            mitem.body = encoded
                            mitem.recipents = mailItem.recipents;
                            mitem.cc = mailItem.cc;
                        
                            setFormData((prev: any) => ({
                                ...prev,
                                mailItem: mitem,
                            }));
                           
                        }
                    });
                    // --------- CC SYNC BLOCK ----------
                    const itemTyped3 = item as any;
                    if (itemTyped3.cc && itemTyped3.cc.length > 0) {
                        const ccOptions = itemTyped3.cc.map((ccItem: any) => ({
                            label: `${ccItem.displayName ? ccItem.displayName : ccItem.emailAddress} <${ccItem.emailAddress}>`,
                            value: valueForEmail(ccItem.emailAddress, emailOptions), // 3-part like send mode
                            native: true
                        }));

                        setSelectedCc(ccOptions);
                        setFormData(prev => ({ ...prev, cc: ccOptions }));
                        SetStorageItem(
                            LOCALSTORAGE_KEYS.SelectedCC,
                            ccOptions.length ? ccOptions.map(opt => opt.label).join(", ") : ""
                        );
                    }

                    //--------- END CC SYNC BLOCK ---------------
                    setIsCcDisabled(!(itemTyped3.cc && itemTyped3.cc.length > 0));
                } else {
                    // development
                    setMailItem({
                        ...mailItem,
                        attachments: [],
                        subject: "Testing Data",
                        sender: "sender@outlook.com",
                        senderDisplayName: "Sender Company",
                        recipents: [
                            { displayName: "rec test", emailAddress: "rec@outlook.com" },
                        ],
                        date: Date.UTC,
                        time: "10:10:10",
                        referenceid: "uniqueid100"
                    });
                    setMailBody("Body");
                }
                //setIsLoader(false);
            } catch (error) {
                console.error("Error initializing Office.js:", error);
            } finally {
            }
        });

        return EmailItemid;
    };

    const formatDate = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, "0"); // Months are zero-based
        const day = String(date.getDate()).padStart(2, "0");
        return `${year}-${month}-${day}`;
    };

    const onCancel = () => {
        // logout();
        localStorage.removeItem(LOCALSTORAGE_KEYS.AccessToken);
        logout({
            logoutParams: { returnTo: window.location.origin },
        });
    };
    const getAccessToken = async () => {
        let token = accessToken;
        try {
            if (!token) {
                // tokenManager covers BOTH login paths: classic-Outlook's
                // session-handle (refreshed via the Public API) and the
                // modern-host auth0-react session via the registered
                // silent-refresh fallback - see src/services/tokenManager.ts.
                // Falling back to getAccessTokenSilently() directly (as
                // before) only ever worked for the modern-host path.
                token = await getManagedAccessToken();
                if (!token) {
                    token = await getAccessTokenSilently(); // do not remove
                }

                if (token != null) {
                    setAccessToken(token);
                }
            }

        } catch (exception) { }
        return token;
    };


    let isLoadingMail = false;
    useEffect(() => {
        setTimeout(() => {
            if (isLoadingMail)
                isLoadingMail = false;
        }, 3000);
    }, [isLoadingMail])

    const LoadStatusResponse = (parsedCommunicationId, parsedTimeSlipId, parsedClarraDocsId) => {
        setFormData((prev: any) => {
            const updatedFormData = {
                ...prev,
                // Update logged states
                isCEmailLogged: parsedCommunicationId > 0,
                isTimeSlipLogged: parsedTimeSlipId > 0,
                isClarraDocsLogged: parsedClarraDocsId > 0,
                // Update IDs
                CommunicationId: parsedCommunicationId,
                TimeSlipId: parsedTimeSlipId,
                ClarraDocsId: parsedClarraDocsId,

                // Update read-only states
                isReadOnly: false, //parsedCommunicationId > 0,
                isTimeSlipReadOnly: false,
                isClarraDocsReadOnly: false,
                // parsedClarraDocsId > 0,

                createTimeslip: false, // parsedTimeSlipId > 0 ? false : prev.createTimeslip,
                createClarraDocs: parsedClarraDocsId > 0 ? false : prev.createClarraDocs,

                // Update overall logged status
                AllLogged: (parsedCommunicationId > 0 && parsedTimeSlipId > 0 && parsedClarraDocsId > 0)
            };

            // Only preserve matter_manually_set if already true or if items are logged
            updatedFormData.matterdoc_manually_set =
                prev.matterdoc_manually_set || parsedClarraDocsId > 0;
            updatedFormData.matterts_manually_set =
                prev.matterts_manually_set || parsedTimeSlipId > 0;

            return updatedFormData;
        });
    }
   
    const ReadEmailOnload2 = async (mode, Selecteditem) => {
        console.log("ReadEmailOnload", mode, Selecteditem.itemId, Selecteditem.internetMessageId);
        let debounceTimer: NodeJS.Timeout | null = null;
        const forceRefreshContext = (): Promise<void> => {
            return new Promise((resolve) => {
                try {
                    Office.context.mailbox.item.loadCustomPropertiesAsync(() => resolve());
                } catch {
                    resolve();
                }
            });
        };
     
        debounceTimer = setTimeout(() => {
            clearFormData();
            isLoadingMail = true;
            ReadEmailItem(mode, Selecteditem)
                .then((itemId) =>
                    forceRefreshContext().then(() => itemId)
                )
                .then((itemId) => {
                    if(Selecteditem.internetMessageId !=null) {
                       console.log("ReadEmailOnload2 - Current email emailaddress:", Selecteditem.sender.emailAddress);
                       return getStatus(itemId, Selecteditem.subject, Selecteditem.sender.emailAddress, Selecteditem.internetMessageId);
                    }
                    else return null;
                })
                .then((response) => {
                    if(response !=null) {
                    //const newItem = Office.context.mailbox.item;
                    console.log("Response ", Selecteditem.itemId, response.messageId);
                    if (response.messageId) {
                        setFormData((prev: any) => ({
                            ...prev,
                            PanelOpenState: ""
                        }));
                        LoadStatusResponse(
                            response.communicationId,
                            response.timeSlipId,
                            response.clarraDocsId
                        );
                        isLoadingMail = false;
                    }
                    else {
                        LoadStatusResponse(0, 0, 0);  
                    }
                     }
                    setIsLoader(false);
                })
                .catch((err) => console.error("Error during item refresh:", err));
        }, 100);

        return Selecteditem.itemId;
    }
     const ReadEmailOnload = async (mode, Selecteditem) => {
        console.log("ReadEmailOnload", mode, Selecteditem.itemId);
        let debounceTimer: NodeJS.Timeout | null = null;
        const forceRefreshContext = (): Promise<void> => {
            return new Promise((resolve) => {
                try {
                    Office.context.mailbox.item.loadCustomPropertiesAsync(() => resolve());
                } catch {
                    resolve();
                }
            });
        };
      
    clearFormData();
    isLoadingMail = true;
    LoadStatusResponse(0, 0, 0);
       // Step 1: Get Status first
       console.log("ReadEmailOnload - Current email emailaddress:", Selecteditem.sender.emailAddress);
        getStatus(
            Selecteditem.itemId,
            Selecteditem.subject,
            Selecteditem.sender.emailAddress,
            Selecteditem.internetMessageId
        )
        .then((response) => {
            console.log("Response ", Selecteditem.itemId, response.messageId);

            // Step 2: Load Status Response
            if (response.messageId !=null) {
                LoadStatusResponse(
                    response.communicationId,
                    response.timeSlipId,
                    response.clarraDocsId
                );
            } else {
                 LoadStatusResponse(0, 0, 0);
                 return forceRefreshContext()
                .then(() => ReadEmailItem(mode, Selecteditem))
                .then((itemId) => {
                    console.log("ReadEmailItem completed:", itemId);
                });
            }
            // Step 3: Read Email Item after loading status
        })
        .then(() => {
            isLoadingMail = false;
            setIsLoader(false);
        })
        .catch((err) => {
            console.error("Error during item refresh:", err);
            setIsLoader(false);
            isLoadingMail = false;
        });



        return Selecteditem.itemId;
    }
     let r2 = false;
    

    useEffect(() => {
        Office.onReady((info) => {
            console.log("Initializing email change detection");

            const supportsItemChanged =
                info.platform === Office.PlatformType.PC || // Classic Windows Desktop
                info.platform === Office.PlatformType.OfficeOnline || // OWA
                info.platform === Office.PlatformType.Universal || // New Outlook (Windows/Mac)
                info.platform === Office.PlatformType.Mac;
               // let  newItem = null;
                 waitForItem().then((newItem)=>{
                    (async () => {
                if (Reset == true) {
                    SetIsReset(true);
                    // let token = getAccessToken(); 
                    SetStorageItem("state", "1");
                    if (isEmailRead == false) {
                       
                        setIsEmailRead(true);
                        if (!r2) {
                            r2 = true;
                            setIsLoader(true);
                            console.log("Default first time load for Message Id when load first time", JSON.stringify(newItem));
                            ReadEmailOnload2(0, newItem)
                                .then((itemId) => {
                                    console.log("Default first time load for Message Id when load first time", itemId);
                                });
                        }
                    }
                }
            })();
               });

                
            let debounceTimer: NodeJS.Timeout | null = null;

          
            const handleItemChanged = (Selecteditem) => {
                setIsLoader(true);
                debounceTimer = setTimeout(() => {
                    setIsLoader(true);
                   // location.
                   console.log("Item Changed Detected", JSON.stringify(Selecteditem));
                    ReadEmailOnload2(1,Selecteditem).then((item) => {
                          console.log("ReadEmailOnload On Item Change ", item);
                    })
                }, 1000);
            };
          
            const isMacOutlook = false; //  info.platform === Office.PlatformType.Mac;
            if (isMacOutlook) {
                console.log("Setting up polling on internetMessageId for NEW Outlook Mac Desktop");
                let lastItemId: string | null = null;
                let pollInterval: NodeJS.Timeout | null = null;

                const pollSelectedItem = () => {
                    Office.context.mailbox.getSelectedItemsAsync(asyncResult => {
                        if (asyncResult.status !== Office.AsyncResultStatus.Succeeded) {
                            console.log("Failed to get selected items:", asyncResult.error);
                            return;
                        }

                        const selectedItems = asyncResult.value;
                        if (!selectedItems || selectedItems.length === 0) return;

                        const currentId = (selectedItems[0] as any).itemId;

                        if (lastItemId && lastItemId !== currentId) {
                            console.log("Item changed detected via polling:", { from: lastItemId, to: currentId });
                            //handleItemChanged();
                           window.location.reload();
                        }

                        lastItemId = currentId;
                    });
                };

                pollInterval = setInterval(pollSelectedItem, 1000);

                // Cleanup
                return () => {
                    if (pollInterval) clearInterval(pollInterval);
                };


            } else if (supportsItemChanged) {
                Office.context.mailbox.removeHandlerAsync(
                    Office.EventType.ItemChanged,
                    () => {
                        Office.context.mailbox.addHandlerAsync(Office.EventType.ItemChanged, (eventChange) => {

                            waitForItem().then((newItem) => {
                                handleItemChanged(newItem);
                            });

                        });
                    }
                );
            }
            else {
                console.warn("Platform does NOT support ItemChanged");
            }
        });
    }, []);
let counter = 0;
const waitForItem = () => {
    return new Promise((resolve) => {
        const tryLoad = () => {
            try {
                const item = Office.context.mailbox.item;
                //console.log("waitForItem", item,item.internetMessageId);
                if ((item && item.itemId) || counter == 3) {
                    resolve(item);
                } else {
                    counter++;
                    setTimeout(tryLoad, 100); // retry every 100ms
                }
            } catch {
                setTimeout(tryLoad, 100);
            }
        };
        tryLoad();
    });
};
//     useEffect(() => {
//         // Office.onReady is async, so a cleanup returned from its callback never reaches
//         // React. Capture it here instead, otherwise the ItemChanged handler outlives the
//         // component and stacks up on remount.
//         let cleanupHandlers: (() => void) | undefined;
//         Office.onReady((info) => {
//             console.log("[EmailLog] Office ready, platform:", info.platform);
//             const supportsItemChanged =
//                 info.platform === Office.PlatformType.PC ||           // Classic Windows Desktop
//                 info.platform === Office.PlatformType.OfficeOnline || // Browser Outlook (OWA) - CRITICAL FOR WEB
//                 info.platform === Office.PlatformType.Universal ||    // New Outlook Desktop
//                 info.platform === Office.PlatformType.Mac;

//             console.log("[EmailLog] supportsItemChanged:", supportsItemChanged);

//             // ✅ FIXED: Initial load of the email when component first mounts
//             let initialLoadDone = false; // Local flag to prevent multiple initial loads

//             waitForItem().then((newItem) => {
//                 (async () => {
//                     if (Reset === true && !initialLoadDone) {
//                         initialLoadDone = true;
//                         SetIsReset(true);
//                         SetStorageItem("state", "1");

//                         // FIX: Check if item is null (new/unsaved email in compose mode)
//                         // if (newItem === null || newItem === undefined) {
//                         //     console.log("[EmailLog] ⚠️  Item is null/undefined - likely composing new email or switching from read to compose mode");
//                         //     setIsLoader(false);
//                         //     setIsEmailRead(false); // Keep as false to wait for actual email
//                         //     return; // Don't try to load null item
//                         // }

//                         if (!(newItem as any)?.itemId) {
//                             console.warn("[EmailLog] ⚠️  Item has no itemId - email not fully created yet. ItemId:", (newItem as any)?.itemId);
//                             setIsLoader(false);
//                             setIsEmailRead(false);
//                             return; // Wait for item to be fully created
//                         }

//                         if (isEmailRead === false) {
//                             setIsEmailRead(true);
//                             setIsLoader(true);
//                             console.log(
//                                 "[EmailLog] Initial email load on first mount - itemId:",
//                                 (newItem as any)?.itemId
//                             );

//                             try {
//                                 await ReadEmailOnload2(0, newItem);
//                                 console.log("[EmailLog] ✓ Initial email load completed successfully");
//                             } catch (error) {
//                                 console.error("[EmailLog] Error in initial email load:", error);
//                                 setIsLoader(false);
//                                 setIsEmailRead(false); // Reset flag so it can retry
//                             }
//                         }
//                     }
//                 })();
//             }).catch((error) => {
//                 console.error("[EmailLog] Error waiting for initial item:", error);
//                 setIsLoader(false);
//             });

//             // ✅ FIXED: Debounce timer for handling multiple rapid item changes
//             let debounceTimer: NodeJS.Timeout | null = null;

//             // ✅ FIXED: Handler function for item changes
//             const handleItemChanged = (selectedItem: any) => {
//                 console.log("[EmailLog] handleItemChanged CALLED with itemId:", selectedItem?.itemId);
//                // debugger;
//                 // Cancel previous debounce if still pending
//                 if (debounceTimer) {
//                     console.log("[EmailLog] Clearing previous debounce timer");
//                     clearTimeout(debounceTimer);
//                 }

//                 setIsLoader(true);
//                 console.log("[EmailLog] setIsLoader(true)");

//                 // Debounce: wait 1 second before processing
//                 // This prevents reloading on every single item change event
//                 debounceTimer = setTimeout(async () => {
//                     console.log(
//                         "[EmailLog] *** DEBOUNCE EXECUTING: Loading email:",
//                         selectedItem?.itemId
//                     );

//                     try {
//                         debugger;
//                         console.log("[EmailLog] Before ReadEmailOnload2, isLoader=true");
//                         await ReadEmailOnload2(1, selectedItem);
//                         console.log("[EmailLog] ✓ ReadEmailOnload2 completed");
//                         setIsLoader(false);
//                     } catch (error) {
//                         console.error("[EmailLog] ✗ ReadEmailOnload2 error:", error);
//                         setIsLoader(false);
//                     }
//                 }, 1000);
//             };

//             // ✅ FIXED: Mac Outlook special handling (uses polling as fallback)
//             const isMacOutlook = false; // TODO: Uncomment if needed: info.platform === Office.PlatformType.Mac;

//             if (isMacOutlook) {
//                 console.log("[EmailLog] Using polling for Mac Outlook");
//                 let lastItemId: string | null = null;
//                 let pollInterval: NodeJS.Timeout | null = null;

//                 const pollSelectedItem = () => {
//                     Office.context.mailbox.getSelectedItemsAsync((asyncResult) => {
//                         if (asyncResult.status !== Office.AsyncResultStatus.Succeeded) {
//                             console.warn("[EmailLog] Poll failed:", asyncResult.error?.message);
//                             return;
//                         }

//                         const selectedItems = asyncResult.value;
//                         if (!selectedItems || selectedItems.length === 0) return;

//                         const currentId = (selectedItems[0] as any).itemId;

//                         if (lastItemId && lastItemId !== currentId) {
//                             console.log("[EmailLog] Item changed via polling:", currentId);
//                             handleItemChanged(selectedItems[0]);
//                         }

//                         lastItemId = currentId;
//                     });
//                 };

//                 pollInterval = setInterval(pollSelectedItem, 1000);

//                 // Cleanup polling on unmount
//                 cleanupHandlers = () => {
//                     if (pollInterval) clearInterval(pollInterval);
//                     if (debounceTimer) clearTimeout(debounceTimer);
//                 };
//             }
//             // ✅ FIXED: CRITICAL FIX - Correct handler registration for browser/web Outlook
//             else if (supportsItemChanged) {
//                 console.log("[EmailLog] Registering ItemChanged event handler for browser Outlook");
// // ✅ FIXED: Protected handler registration with retry logic and duplicate prevention
//                 let handlerRegistered = false;
//                 let registrationAttempts = 0;
//                 const MAX_ATTEMPTS = 3;

//                 const attemptHandlerRegistration = () => {
//                     if (handlerRegistered) {
//                         console.log("[EmailLog] Handler already registered, skipping duplicate attempt");
//                         return;
//                     }

//                     registrationAttempts++;
//                     console.log(`[EmailLog] Attempting handler registration (attempt ${registrationAttempts}/${MAX_ATTEMPTS})`);

//                     // First, remove any existing handler to prevent duplicates
//                     Office.context.mailbox.removeHandlerAsync(
//                         Office.EventType.ItemChanged,
//                         {},
//                         (removeResult) => {
//                             console.log("[EmailLog] Old handler removed (if any existed)");

//                             // Now add fresh handler
//                             Office.context.mailbox.addHandlerAsync(
//                                 Office.EventType.ItemChanged,
//                                 (eventData) => {
//                                     console.log("[EmailLog] *** ItemChanged event FIRED ***", new Date().toISOString());
//                                     console.log("[EmailLog] Event data:", eventData);

//                                     // When item changes, get the new item
//                                     console.log("[EmailLog] Calling waitForItem()...");
//                                     waitForItem()
//                                         .then((newItem) => {
//                                             console.log("[EmailLog] waitForItem() resolved, newItem:", (newItem as any)?.itemId);

//                                             // ✅ FIX: Check if item is null or missing itemId (compose mode)
//                                             if (!newItem) {
//                                                 console.warn("[EmailLog] ⚠️  ItemChanged: newItem is null - likely in compose mode");
//                                                 return;
//                                             }

//                                             if (!(newItem as any)?.itemId) {
//                                                 console.warn("[EmailLog] ⚠️  ItemChanged: newItem has no itemId - likely in compose mode");
//                                                 return;
//                                             }

//                                             console.log("[EmailLog] Calling handleItemChanged with:", (newItem as any)?.itemId);
//                                             handleItemChanged(newItem);
//                                         })
//                                         .catch((error) => {
//                                             console.error("[EmailLog] Error getting item after change:", error);
//                                         });
//                                 },
//                                 (asyncResult) => {
//                                     debugger;
//                                     if (asyncResult.status === Office.AsyncResultStatus.Succeeded) {
//                                         handlerRegistered = true;
//                                         console.log("[EmailLog] ✓ ItemChanged handler registered successfully");
//                                     } else {
//                                         console.error(
//                                             "[EmailLog] ✗ Failed to register ItemChanged handler:",
//                                             asyncResult.error?.message
//                                         );

//                                         // Retry logic: try again after a delay if registration failed
//                                         if (registrationAttempts < MAX_ATTEMPTS) {
//                                             console.log(`[EmailLog] Retrying handler registration in 1 second...`);
//                                             setTimeout(() => {
//                                                 attemptHandlerRegistration();
//                                             }, 1000);
//                                         } else {
//                                             console.error("[EmailLog] Max registration attempts reached. Handler registration failed.");
//                                         }
//                                     }
//                                 }
//                             );
//                         }
//                     );
//                 };

//                 // Start registration attempt
//                 attemptHandlerRegistration();

//                 // Cleanup on unmount
//                 cleanupHandlers = () => {
//                     if (debounceTimer) clearTimeout(debounceTimer);

//                     try {
//                         Office.context.mailbox.removeHandlerAsync(
//                             Office.EventType.ItemChanged,
//                             {},
//                             () => {
//                                 console.log("[EmailLog] ItemChanged handler removed on component unmount");
//                             }
//                         );
//                     } catch (error) {
//                         console.error("[EmailLog] Error removing handler on unmount:", error);
//                     }
//                 };
//             }
//             // Fallback for unsupported platforms
//             else {
//                 console.warn(
//                     "[EmailLog] Platform does NOT support ItemChanged event:",
//                     info.platform
//                 );
//             }
//         });

//         return () => {
//             if (cleanupHandlers) cleanupHandlers();
//         };
//     }, []);
// The retry budget has to be per call. A shared counter stays exhausted after the
// first slow item, so every later ItemChanged resolves on the first tick with a stale
// or null item - the exact failure a pinned pane provokes, since it fires this on
// every message the user clicks instead of once per pane load.
// const WAIT_FOR_ITEM_MAX_ATTEMPTS = 3;
// const waitForItem = () => {
//     return new Promise((resolve) => {
//         let attempts = 0;
//         const tryLoad = () => {
//             try {
//                 const item = Office.context.mailbox.item;
//                 if (item && item.itemId) {
//                     resolve(item);
//                     return;
//                 }
//             } catch {
//                 // item not reachable yet - fall through to the retry below
//             }

//             if (attempts >= WAIT_FOR_ITEM_MAX_ATTEMPTS) {
//                 // Callers must handle null; resolving the stale item instead would
//                 // render the previously selected message against the new selection.
//                 resolve(null);
//                 return;
//             }

//             attempts++;
//             setTimeout(tryLoad, 100); // retry every 100ms
//         };
//         tryLoad();
//     });
// };

    useEffect(() => {
        if (logStatus != null) {
            LoadStatusResponse(logStatus.communicationId, logStatus.timeSlipId, logStatus.clarraDocsId);
        }

    }, [logStatus]);


    const getAllMatter = () => {
        const selectedMatterMemory = null; //getStorageItem("SelectedMatter");
        if (selectedMatterMemory != null) {
            const findMatter = mattersoptions.find(item => item.label == selectedMatterMemory);
            if (findMatter != null)
                setSelectedMatter(findMatter);
            setFormData((prev: any) => ({
                ...prev,
                matter: selectedMatterMemory,
                matterid:0
            }));
            setFormData((prev: any) => ({
                ...prev,
                matterdoc: selectedMatterMemory,
                matteriddoc : 0
            }));
            setFormData((prev: any) => ({
                ...prev,
                matterts: selectedMatterMemory,
                matteridts : 0
            }));
        }
        else {
            setSelectedMatter(null);
            setFormData((prev: any) => ({
                ...prev,
                matter: null
            }));
            setFormData((prev: any) => ({
                ...prev,
                matterdoc: null,
            }));
            setFormData((prev: any) => ({
                ...prev,
                matterts: null,
            }));
        }
    };
    useEffect(() => {
        if (mattersNextPage < mattersPageCount) {
            let Pagenum = mattersNextPage + 1;
            const matterParam = {
                'AccessTokenParam': accessToken,
                'NextPage': Pagenum
            };
            setTimeout(() => {
                dispatch(fetchAllMatters(matterParam));
            }, 100);
        }
    }, [matters]);

    useEffect(() => {
        if (contactsNextPage < contactsPageCount) {
            let Pagenum = contactsNextPage + 1;
            const contactParam = {
                'AccessTokenParam': accessToken,
                'NextPage': Pagenum
            };
            dispatch(fetchAllContacts(contactParam));
        }
    }, [contacts]);

    useEffect(() => {
        setFormData((prev: any) => ({
            ...prev,
            date: (CreateDate) ? CreateDate : new Date(),
        }));

        setFormData((prev: any) => ({
            ...prev,
            datedoc: (CreateDate) ? CreateDate : new Date(),
        }));

        setFormData((prev: any) => ({
            ...prev,
            datets: (CreateDate) ? CreateDate : new Date(),
        }));
    }, [CreateDate]);


    useEffect(() => {
        setEmailLogged(false);
        if (communicationDetail != null) {
            if (communicationDetail.id > 0) {
                setEmailLogged(true);
                setIsLoader(false);
                setFormData((prev: any) => ({
                    ...prev,
                    isEmailLogged: true
                }));
                //formData.isEmailLogged = true;
            } else {
                setIsLoader(false);
                SetIsReset(false);
            }
        } else if (isEmailRead == false) {
            SetIsReset(true);
            setIsEmailRead(true);
        }
    }, [communicationDetail]);

   

    const readCustomPropertiesAndUpdateUI = (ItemId: string) => {
        console.log("Reading custom properties and updating UI");

        Office.context.mailbox.item.loadCustomPropertiesAsync(res => {
            if (res.status === Office.AsyncResultStatus.Succeeded) {
                const props = res.value;
                // Get IDs from custom properties
                const communicationId = props.get(`communicationId${tenantId}`);
                const timeSlipId = props.get(`timeSlipId${tenantId}`);
                const clarraDocsId = props.get(`clarraDocsId${tenantId}`);

                console.log("Reading from custom properties:", {
                    communicationId,
                    timeSlipId,
                    clarraDocsId
                });

                // Parse values (they're stored as strings)
                const parsedCommunicationId = (communicationId && communicationId !== "null") ? parseInt(communicationId) : 0;
                const parsedTimeSlipId = (timeSlipId && timeSlipId !== "null") ? parseInt(timeSlipId) : 0;
                const parsedClarraDocsId = (clarraDocsId && clarraDocsId !== "null") ? parseInt(clarraDocsId) : 0;

                // First update logged states
                setFormData((prev: any) => {
                    const updatedFormData = {
                        ...prev,
                        // Update logged states
                        isCEmailLogged: parsedCommunicationId > 0,
                        isTimeSlipLogged: parsedTimeSlipId > 0,
                        isClarraDocsLogged: parsedClarraDocsId > 0,

                        // Update IDs
                        CommunicationId: parsedCommunicationId,
                        TimeSlipId: parsedTimeSlipId,
                        ClarraDocsId: parsedClarraDocsId,

                        // Update read-only states
                        isReadOnly: false, //parsedCommunicationId > 0,
                        // isTimeSlipReadOnly: parsedTimeSlipId > 0,
                        isClarraDocsReadOnly: false, // parsedClarraDocsId > 0,
                        createTimeslip: parsedTimeSlipId > 0 ? false : prev.createTimeslip,
                        createClarraDocs: parsedClarraDocsId > 0 ? false : prev.createClarraDocs,

                        // Update overall logged status
                        AllLogged: (parsedCommunicationId > 0 && parsedTimeSlipId > 0 && parsedClarraDocsId > 0)
                    };

                    // Only preserve matter_manually_set if already true or if items are logged
                    updatedFormData.matterdoc_manually_set =
                        prev.matterdoc_manually_set || parsedClarraDocsId > 0;
                    updatedFormData.matterts_manually_set =
                        prev.matterts_manually_set || parsedTimeSlipId > 0;

                    return updatedFormData;
                });

                // Update UI states
                setEmailLogged(parsedCommunicationId > 0);

                
                if (parsedCommunicationId > 0) {
                    getCommunicationDetailId(parsedCommunicationId.toString(), ItemId)
                        .then(data => {
                            if (data) {
                                // Communication details loaded - update UI
                                // console.log("Communication details loaded, updating UI");
                            }
                        });
                }
                if (parsedTimeSlipId > 0) {
                    getTimeSlipDetailId(parsedTimeSlipId.toString(), ItemId)
                        .then(data => {
                            if (data) {
                                // TimeSlip details loaded - update UI
                                // console.log("TimeSlip details loaded, updating UI");
                            }
                        });
                }

                if (parsedClarraDocsId > 0) {
                    getClarraDocsDetailId(parsedClarraDocsId.toString(), ItemId)
                        .then(data => {
                            if (data) {
                                // ClarraDocs details loaded - update UI
                                // console.log("ClarraDocs details loaded, updating UI");
                            }
                        });
                }
            } else {
                // Reset to default states on error
                setFormData(prev => ({
                    ...prev,
                    isCEmailLogged: false,
                    isTimeSlipLogged: false,
                    isClarraDocsLogged: false,
                    isReadOnly: false,
                    isTimeSlipReadOnly: false,
                    isClarraDocsReadOnly: false,
                    createCommunication: true,
                    createTimeslip: false,
                    createClarraDocs: false,
                    AllLogged: false
                }));
            }
        });
    };

    
    useEffect(() => {
     
        if (formData.PanelOpenState == "panel1") {
            if (formData.CommunicationId != null && formData.CommunicationId > 0) {
                setIsLoader(true);
                // console.log("UseEffect Load Communication Detail - ", formData.CommunicationId, formData.mailItem.referenceid)
                GetDetails(formData.CommunicationId, 0, 0, formData.mailItem.referenceid);
            }
            else {
                GetDetails(0, 0, 0, formData.mailItem.referenceid);
            }
        }
        else if (formData.PanelOpenState == "panel2") {
            if (formData.TimeSlipId != null && formData.TimeSlipId > 0) {
                setIsLoader(true);
                GetDetails(0, formData.TimeSlipId, 0, formData.mailItem.referenceid);
            }
            else {
                GetDetails(0, 0, 0, formData.mailItem.referenceid);
            }
        }
        else if (formData.PanelOpenState == "panel3") {
            if (formData.ClarraDocsId != null && formData.ClarraDocsId > 0) {
                setIsLoader(true);
                GetDetails(0, 0, formData.ClarraDocsId, formData.mailItem.referenceid);
            }
            else {
                GetDetails(0, 0, 0, formData.mailItem.referenceid);
            }
        }
        else {
            setIsLoader(false);
            // console.log("Panel Selected = ", formData.PanelOpenState);
        }

    }, [formData.PanelOpenState]);

  
    const GetDetails = async (communicationId, timeSlipId, clarraDocsId, ItemId) => {
        const parsedCommunicationId = (communicationId && communicationId !== "null") ? parseInt(communicationId) : 0;
        const parsedTimeSlipId = (timeSlipId && timeSlipId !== "null") ? parseInt(timeSlipId) : 0;
        const parsedClarraDocsId = (clarraDocsId && clarraDocsId !== "null") ? parseInt(clarraDocsId) : 0;
        
        if (parsedCommunicationId > 0) {
            getCommunicationDetailId(parsedCommunicationId.toString(), ItemId)
                .then(data => {
                    setIsLoader(false);
                    if (data) {
                        console.log("Communication details loaded");

                        setEmailLogged(parsedCommunicationId > 0);
                    }
                });
        }
        else {
            //Need to check if Communication ID exists in databse with Selected MessgaeId
            //Logged communication Successfull >>  Still visible
        }

        if (parsedTimeSlipId > 0) {
            getTimeSlipDetailId(parsedTimeSlipId.toString(), ItemId)
                .then(data => {
                    setIsLoader(false);
                    if (data) {
                        console.log("TimeSlip details loaded");

                    }
                });
        }


        if (parsedClarraDocsId > 0) {
            getClarraDocsDetailId(parsedClarraDocsId.toString(), ItemId)
                .then(data => {
                    setIsLoader(false);
                    if (data) {
                        // ClarraDocs details loaded - update UI
                        console.log("ClarraDocs details loaded");
                    }
                });
        }

    };

    

    useEffect(() => {
        if (false) {
            if (logStatus != null && Object.keys(logStatus).length > 0) {
                if (logStatus.communicationId && logStatus.communicationId > 0) {
                    console.log("LogStatus contains communicationId, calling getCommunicationDetailId:", logStatus.communicationId);

                    getCommunicationDetailId(logStatus.communicationId.toString(), "");
                }

                if (logStatus.timeSlipId && logStatus.timeSlipId > 0) {
                    console.log("LogStatus contains timeSlipId, calling getTimeSlipDetailId:", logStatus.timeSlipId);
                    getTimeSlipDetailId(logStatus.timeSlipId.toString(), "");
                }

                if (logStatus.clarraDocsId && logStatus.clarraDocsId > 0) {
                    console.log("LogStatus contains clarraDocsId, calling getClarraDocsDetailId:", logStatus.clarraDocsId);
                    getClarraDocsDetailId(logStatus.clarraDocsId.toString(), "");
                }

                // Only update ClarraDocs status from API response, don't override custom properties
                setFormData((prev: any) => {
                   

                    // Check if ClarraDocs is being loaded from API
                    const isClarraDocsLoggedFromAPI = logStatus.clarraDocsId != null && logStatus.clarraDocsId > 0;

                    const updatedData = {
                        ...prev,
                        // Only update ClarraDocs from API, preserve custom properties data
                        isClarraDocsLogged: isClarraDocsLoggedFromAPI || prev.isClarraDocsLogged,
                        clarraDocsId: isClarraDocsLoggedFromAPI ? logStatus.clarraDocsId : prev.clarraDocsId,
                        // Set matterdoc_manually_set to true if ClarraDocs is logged from API
                        matterdoc_manually_set: prev.matterdoc_manually_set || isClarraDocsLoggedFromAPI,
                        // Update AllLogged considering all three statuses
                        AllLogged: (prev.isCEmailLogged && prev.isTimeSlipLogged && (prev.isClarraDocsLogged || isClarraDocsLoggedFromAPI)),
                    };

                  
                    return updatedData;
                });

               

            } else {
                console.log("LogStatus is null or empty, skipping update to preserve custom properties data");
            }
        }
    }, [logStatus]);

    // pinnable update effect
    useEffect(() => {
        // Sync logged states with UI
        if (formData.isCEmailLogged !== isEmailLogged) {
            setEmailLogged(formData.isCEmailLogged);
        }

       
    }, [formData.isCEmailLogged, formData.isTimeSlipLogged, formData.isClarraDocsLogged]);

    useEffect(() => {

        if (formData?.isCEmailLogged) return;

        const x = getAccessToken();
        if (mailItem.sender) {
            const objfrom = {
                label: mailItem.senderDisplayName + " <" + mailItem.sender + ">",
                value: "0|",
            };

            if (emailOptions && emailOptions.length > 0) {
                if (
                    emailOptions.find((item) =>
                        //item.label == mailItem.sender) == null
                        item.label === mailItem.sender ||
                        (typeof item.label === "string" && item.label.includes(mailItem.sender) ==
                            null))
                ) {
                    emailOptions.push(objfrom);
                }
            }

            if (objfrom.label != null && objfrom.label != "")
                setSelectedFrom(objfrom);
        }
        if (mailItem && mailItem.recipents && mailItem.recipents.length > 0) {
            // Deduplicate by email
            const seenEmails = new Set();
            const toOptions = mailItem.recipents.map((item) => {
                const email = (item.emailAddress || "").trim().toLowerCase();
                if (seenEmails.has(email)) return null;
                seenEmails.add(email);

                // Try to find label from emailOptions
                const found = emailOptions.find(opt => {
                    const m = typeof opt.label === "string" ? opt.label.match(/<([^>]+)>/) : null;
                    return m && m[1].trim().toLowerCase() === email;
                });
                const label = found
                    ? found.label
                    : ((item.displayName) ? item.displayName : item.emailAddress) + " <" + item.emailAddress + ">";
                return {
                    label,
                    value: found ? found.value : (item.value != null ? item.value : "0|"),
                    native: true
                };
            }).filter(Boolean);

            setSelectedTo(toOptions);
            setFormData(prev => ({ ...prev, recipents: toOptions }));
            SetStorageItem(
                LOCALSTORAGE_KEYS.SelectedTO,
                toOptions.length ? toOptions.map(opt => opt.label).join(", ") : ""
            );
        }

        
    }, [dispatch, mailItem, emailbody, emailOptions]);

    // obselete method
    const GetContactId = async (email) => {
        let Id = contacts
            .flatMap((item) =>
                item.contactEmails.map((emailObj) => ({
                    id: item.id,
                    userId: item.userId,
                    emailObj: emailObj
                }))
            )
            .filter((entry) => entry.emailObj && entry.emailObj.email == email)
            .map((entry) => ({
                value: entry.id + "|" + entry.userId
            }));

        if (Id != null && Id.length > 0)
            return Id[0];
        else
            return "0|";
    }

    const onSaveform = () => {
        const Mode: any = {
            cm: formData.createCommunication,
            tm: formData.createTimeslip,
            dm: formData.createClarraDocs,
        };
        const isValid = validateForm(Mode);

        if (isValid) {

            mailItem.attachments = files;

            mailItem.recipents = selectedTo.map((item) => {
                return { emailAddress: item.label, id: item.value };
            });

            mailItem.cc = selectedCc.map((item) => {
                return { emailAddress: item.label, id: item.value };
            });
            
            onSubmit(
                {
                    ...formData,
                    mailItem: mailItem,
                    mailbody: emailbody,
                    matter: selectedMatter,
                    tag: selectedTags,
                    Token: accessToken,
                },
                Mode
            );
            setIsEmailRead(false);
        }
    };

    // Matter data for dropdowns

    // Memoised: this was a plain `let ... = matters.map(...)`, so it rebuilt
    // every option object (up to ~50,000 of them) on EVERY render of this
    // form, and handed the Dropdown a new array identity each time - which
    // also defeats the React.memo on Dropdown. `emailOptions` above was
    // already memoised; this one had been missed.
    const mattersoptions = useMemo(() =>
        matters.map((matter) => ({
            value: matter.id,
            label: matter.matter + " (" + matter.number + ")",
        }))
        , [matters]);


    const sampleTags = useMemo(() =>
        tags.map((tag) => ({
            value: tag.id,
            label: tag.name,
        }))
        , [tags]);

    const handleFromChange = (selectedOption: any) => {
        setSelectedFrom(selectedOption);
    };
    
    function labelToRecipient(label) {
        const email = label.substring(label.lastIndexOf("<") + 1, label.lastIndexOf(">")).trim();
        const displayName = label.split(" <")[0].trim();
        return { emailAddress: email, displayName: displayName };
    }

    // --- Outlook to dropdown format
    function recipientToLabel(recipient) {
        const label = `${recipient.displayName} <${recipient.emailAddress}>`;
        return { label, value: recipient.emailAddress };
    }

   
    const handleToChange = (selectedOptions) => {
        const uniqueSelected = Array.from(new Set((selectedOptions || []).map(opt => opt.label)))
            .map(label => (selectedOptions || []).find(opt => opt.label === label));

        setSelectedTo(uniqueSelected);
        setFormData(prev => ({ ...prev, recipents: uniqueSelected }));

        // Only here: update Outlook's TO field
        if (Office.context.mailbox.item.to && Office.context.mailbox.item.to.setAsync) {
            const recipients = uniqueSelected.map(opt => labelToRecipient(opt.label));
            console.log("Setting Office To:", recipients);
            Office.context.mailbox.item.to.setAsync(recipients, (res) => {
                if (res.status !== Office.AsyncResultStatus.Succeeded) {
                    console.warn("[Clarra] to.setAsync failed:", res.error);
                }
            });
        }
    };

    const handleCcChange = (selectedOptions) => {
        const uniqueSelected = Array.from(new Set((selectedOptions || []).map(opt => opt.label)))
            .map(label => (selectedOptions || []).find(opt => opt.label === label));
        // Map to Office object with correct displayName logic
        const ccRecipients = uniqueSelected.map(opt => labelToRecipient(opt.label));
        if (Office.context.mailbox.item.cc && Office.context.mailbox.item.cc.setAsync) {
            Office.context.mailbox.item.cc.setAsync(ccRecipients, (res) => {
                if (res.status !== Office.AsyncResultStatus.Succeeded) {
                    console.warn("[Clarra] cc.setAsync failed:", res.error);
                }
            });
        }
        setSelectedCc(uniqueSelected);
        setFormData((prev) => ({
            ...prev,
            cc: uniqueSelected,
        }));
    };
   
    const handleMatterChange = (value: any) => {
        setSelectedMatter(value);
        console.log("Selected Matter ", value);
        const matterLabel = value ? value.label : null;
        const matterid = value ? value.value : 0;
        SetStorageItem(LOCALSTORAGE_KEYS.SelectedMatter, matterLabel);
        SetStorageItem(LOCALSTORAGE_KEYS.SelectedMatterID, matterid);

        setFormData((prev: any) => {
            const newFormData = {
                ...prev,
                matter: matterLabel, // Always update communication matter
                matterid : matterid
            };

            // Only update ClarraDocs and TimeSlip matters if they haven't been logged yet
            // AND if they haven't been manually set by the user in their respective forms
            if (!prev.isClarraDocsLogged && !prev.matterdoc_manually_set) 
            {
                newFormData.matterdoc = matterLabel;
                newFormData.matteriddoc = matterid
            }
            if (!prev.isTimeSlipLogged && !prev.matterts_manually_set) {
                newFormData.matterts = matterLabel;
                newFormData.matteridts = matterid
            }

            return newFormData;
        });
    };

    // Track when TimeSlip or ClarraDocs matters are manually set
    useEffect(() => {
        // Listen for changes to matterts that don't come from communication matter changes
        if (formData.matterts && formData.matterts !== formData.matter) {
            setFormData(prev => ({
                ...prev,
                matterts_manually_set: true
            }));
        }
    }, [formData.matterts]);

    useEffect(() => {
        // Listen for changes to matterdoc that don't come from communication matter changes
        if (formData.matterdoc && formData.matterdoc !== formData.matter) {
            setFormData(prev => ({
                ...prev,
                matterdoc_manually_set: true
            }));
        }
    }, [formData.matterdoc]);

    const handleTagsChange = (value: any[]) => {
        setSelectedTags(value);
        SetStorageItem(LOCALSTORAGE_KEYS.SelectedTags,
            (value != null && value.length > 0) ?
                value.map((item) => item.label).join(", ") : "");
        setFormData((prev: any) => ({
            ...prev,
            tag: value,
        }));
        setFormData((prev: any) => ({
            ...prev,
            tagdoc: value,
        }));
       
    };
   
    const [selectedMatter, setSelectedMatter] = useState(null);
    const [selectedTags, setSelectedTags] = useState([]);
    const [hasAttachment, setHasAttachment] = useState(false);
    const [isComposeMode, setCompodeMode] = useState(false);

    // Timeline of the two values the live-sync effect depends on. If isComposeMode never
    // becomes true, or contacts stays 0, the RecipientsChanged handler is never registered.
   
    useEffect(() => {
       
        if (isComposeMode) return;
        if (!mailItem || !mailItem.cc || mailItem.cc.length === 0 || !emailOptions || emailOptions.length === 0) return;

        // Deduplicate by email address (case-insensitive)
        const seenEmails = new Set();
        const ccOptions = mailItem.cc.map((ccItem) => {
            const email = (ccItem.emailAddress || "").trim().toLowerCase();
            if (seenEmails.has(email)) return null;
            seenEmails.add(email);

            // Try to find label from emailOptions
            const found = emailOptions.find(opt => {
                const m = typeof opt.label === "string" ? opt.label.match(/<([^>]+)>/) : null;
                return m && m[1].trim().toLowerCase() === email;
            });
            const label = found
                ? found.label
                : ((ccItem.displayName) ? ccItem.displayName : ccItem.emailAddress) + " <" + ccItem.emailAddress + ">";
            return {
                label,
                value: found ? found.value : (ccItem.value != null ? ccItem.value : "0|"),
                native: true
            };
        }).filter(Boolean);

        setSelectedCc(ccOptions);
        setFormData(prev => ({ ...prev, cc: ccOptions }));
       
    }, [mailItem.cc, emailOptions, isComposeMode]);

    // --- ATTACHMENTS SYNC FOR COMPOSE MODE ---
    useEffect(() => {
        if (!isComposeMode || !Office.context.mailbox.item) return;

        const updateAttachments = () => {
            Office.context.mailbox.item.getAttachmentsAsync((result) => {
                if (result.status === Office.AsyncResultStatus.Succeeded) {
                    const attachments = (result.value || []).filter(a => !a.isInline);
                    const formatted = attachments.map(a => ({
                        filename: a.name
                            
                    }));
                    console.log("attachmentdoc- formatted",formatted);
                    SetFiles(formatted);
                    // Always update attachmentsdoc, even if empty
                    setFormData(prev => ({
                        ...prev,
                        attachmentsdoc: formatted
                    }));
                }
            });
        };

        updateAttachments();

        Office.context.mailbox.item.addHandlerAsync(
            Office.EventType.AttachmentsChanged,
            updateAttachments
        );

        return () =>
            Office.context.mailbox.item.removeHandlerAsync(
                Office.EventType.AttachmentsChanged,
                updateAttachments
            );
    }, [isComposeMode, setFormData, SetFiles]);
    // // --- END ATTACHMENTS SYNC ---

    useEffect(() => {
        if (!isComposeMode) return;

        let lastSubject = "";
        const item = Office.context.mailbox.item;

        const pollSubject = () => {
            if (item && item.subject && item.subject.getAsync) {
                item.subject.getAsync((result) => {
                    if (result.status === Office.AsyncResultStatus.Succeeded) {
                        const subject = result.value;
                        if (typeof subject === "string" && subject !== lastSubject) {
                            lastSubject = subject;
                            setFormData((prev: any) => ({
                                ...prev,
                                description: subject,
                                descriptiondoc: subject,
                                emlFileName: subject
                                    ? ((subject.length > 50 ? subject.substring(0, 47) : subject) + ".eml")
                                    : ".eml file will attached when Send"
                            }));
                        }
                    }
                });
            }
        };

        const intervalId = setInterval(pollSubject, 1000);
        pollSubject();

        return () => clearInterval(intervalId);
    }, [isComposeMode, setFormData]);

    let StatusAbortController: AbortController | null = null;
    const getStatus = async (EmailItemId: string, subject: string, sender: string, InternetMessageId:string) => {
        try {
            if(sender == null || sender == "")
            {
                sender = "no-sender";
            }
            const token = await getAccessToken();
            if (!token) {
                console.error("No auth token found for communication detail API");
                return null;
            }

            const API_BASE_URL1 = API_BASE_URL;
            const apiUrl = `${API_BASE_URL1}${GetStatus}`;

            if (StatusAbortController) {
                StatusAbortController.abort();
                console.log("Abort Previous request");
            }
            StatusAbortController = new AbortController();
            
            const response = await fetch(apiUrl, {
                method: 'GET',
                headers: {
                    Authorization: `Bearer ` + token,
                    "Content-Type": "application/json",
                    Accept: "application/json",
                    ExternalId: encodeURIComponent(EmailItemId),
                    InternetMessageId : encodeURIComponent(InternetMessageId),
                    Subject :encodeURIComponent(subject),
                    Sender:encodeURIComponent(sender),
                    'mode': "outlook",
                },
                signal: StatusAbortController.signal,
            });

            if (response.ok) {
                let data = await response.json();
                console.log("Reponse of Status", data);
                // Extract matter and tags data and update form
                return data;
            } else {
                console.error("Communication Status:", response.status, response.statusText);
                return null;
            }
        } catch (error) {
            console.error("Error calling Status Detail API:", error);
            return null;
        }
    };
    let communicationAbortController: AbortController | null = null;
    // Function to get communication details by ID
    const getCommunicationDetailId = async (communicationId: string, ItemId) => {
        try {
            const token = await getAccessToken();
            if (!token) {
                console.error("No auth token found for communication detail API");
                return null;
            }

            const API_BASE_URL1 = getStorageItem(LOCALSTORAGE_KEYS.apibaseurl) || API_BASE_URL;
            const apiUrl = `${API_BASE_URL1}Communications/Detail/${communicationId}`;
            if (communicationAbortController) {
                communicationAbortController.abort();
                console.log("Abort Previous request");
            }
            communicationAbortController = new AbortController();
           
            const response = await fetch(apiUrl, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                },
                signal: communicationAbortController.signal,
            });

            if (response.ok) {
                let data = await response.json();
                if (communicationId == data.id) {
                    // Extract matter and tags data and update form
                    if (data) {
                        const APIResp =
                        {
                            id: data.id,
                            subject: data.subject,
                            MessageId: ItemId
                        };
                       // console.log("EmailAPIObject", APIResp);
                        SetStorageItem("EmailAPIObject", JSON.stringify(APIResp));

                        // Prepare matter and tags data
                        const matterData = data.matter ? {
                            value: data.matter.matterId,
                            label: `${data.matter.matterName} (${data.matter.matterNumber})`
                        } : null;

                        const tagsData = data.communicationTags ? data.communicationTags.map(tag => ({
                            value: tag.tagID,
                            label: tag.tagName
                        })) : [];

                        // Update selectedMatter and selectedTags states (only these should be updated)
                        if (matterData) {
                            setSelectedMatter(matterData);
                            //console.log("Set selectedMatter:", matterData);
                        }

                        if (tagsData.length > 0) {
                            setSelectedTags(tagsData);
                            // console.log("Set selectedTags:", tagsData);
                        }

                        // Update form data with matter and tags only (don't interfere with FROM/TO)
                        setFormData((prev: any) => {
                            const updatedFormData = {
                                ...prev,
                                // Set matter data
                                matter: matterData ? matterData.label : null,
                                matterid : matterData ? matterData.value : 0,
                                // Only update ClarraDocs matter if it hasn't been manually set
                                ...(prev.matterdoc_manually_set ? {} : {
                                    matterdoc: matterData ? matterData.label : null,
                                    matteriddoc : matterData ? matterData.value : 0,
                                }),

                                // Only update TimeSlip matter if it hasn't been manually set
                                ...(prev.matterts_manually_set ? {} : {
                                    matterts: matterData ? matterData.label : null,
                                    matteridts : matterData ? matterData.value : 0,

                                }),

                                tag: tagsData,
                                isReadOnly: prev.isReadOnly || true,
                            };

                            return updatedFormData;
                        });

                        try {
                            const fromOption = data.sender ? toDropdownOptionFromPerson(data.sender) : null;

                            const toOptionsRaw = Array.isArray(data.recipients)
                                ? data.recipients.filter(r => r && r.fullNameWithEmail).map(toDropdownOptionFromPerson)
                                : [];

                            const ccOptionsRaw = Array.isArray(data.cc)
                                ? data.cc.filter(c => c && c.fullNameWithEmail).map(toDropdownOptionFromPerson)
                                : [];

                            const toOptions = dedupeByEmail(toOptionsRaw);
                            const ccOptions = dedupeByEmail(ccOptionsRaw);

                            if (fromOption) {
                                setSelectedFrom(fromOption);
                            }
                            if (toOptions.length > 0) {
                                setSelectedTo(toOptions);
                            }
                            if (ccOptions.length > 0)
                                setSelectedCc(ccOptions);
                            setFormData(prev => ({
                                ...prev,
                                recipents: toOptions,
                                cc: ccOptions,
                            }));

                            SetStorageItem(
                                LOCALSTORAGE_KEYS.SelectedTO,
                                toOptions.length ? toOptions.map(o => o.label).join(", ") : ""
                            );
                            if (ccOptions.length > 0) {
                                SetStorageItem(
                                    LOCALSTORAGE_KEYS.SelectedCC,
                                    ccOptions.length ? ccOptions.map(o => o.label).join(", ") : ""
                                );
                            }
                        } catch (e) {
                            console.error("Failed to map API sender/recipients/cc to UI:", e);
                        }
                    }
                    return data;
                } else {
                    console.error("Communication Detail API failed:", response.status, response.statusText);
                    return null;
                }
            } else {
                clearFormData();
                ReadEmailItem(0, Office.context.mailbox.item);
                console.error("Communication Detail API failed:", response.status, response.statusText);
                return null;
            }
        } catch (error) {
            console.error("Error calling Communication Detail API:", error);
            return null;
        }
    };

    // Function to get timeSlip details by ID
    let timeSlipAbortController: AbortController | null = null;
    const getTimeSlipDetailId = async (timeSlipId: string, ItemId: string) => {
        try {
            const token = await getAccessToken();
            if (!token) {
                console.error("No auth token found for timeSlip detail API");
                return null;
            }

            const API_BASE_URL1 = getStorageItem(LOCALSTORAGE_KEYS.apibaseurl) || API_BASE_URL;
            const apiUrl = `${API_BASE_URL1}TimeSlip/GetTimeSlip/${timeSlipId}`;
            if (timeSlipAbortController) {
                timeSlipAbortController.abort();
                console.log("Abort Previous request");
            }
            timeSlipAbortController = new AbortController();
            //console.log("Calling TimeSlip Detail API:", apiUrl);

            const response = await fetch(apiUrl, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                },
                signal: timeSlipAbortController.signal
            });

            if (response.ok) {
                const data = await response.json();
                if (data) {
                    const matterData = data.matter && data.matterNumber ? {
                        value: data.matterId,
                        label: `${data.matter} (${data.matterNumber})`
                    } : null;

                    // Prepare Activity Code data (from "type" field)
                    const activityCodeData = data.type ? data.type : null;

                    // Prepare Task Code data (from "project" field)
                    const taskCodeData = data.project ? data.project : null;

                    // Convert date from MM/DD/YYYY to DD-MM-YYYY format
                    const formatDateToDDMMYYYY = (dateString) => {
                        if (!dateString) return null;
                        try {
                            // Parse the date string (assuming MM/DD/YYYY format from API)
                            const date = new Date(dateString);
                            if (isNaN(date.getTime())) return null;

                            const day = date.getDate() < 10 ? '0' + date.getDate() : String(date.getDate());
                            const month = (date.getMonth() + 1) < 10 ? '0' + (date.getMonth() + 1) : String(date.getMonth() + 1);
                            const year = date.getFullYear();

                            return `${day}-${month}-${year}`;
                        } catch (error) {
                            console.error("Error formatting date:", error);
                            return null;
                        }
                    };

                    const formattedDate = formatDateToDDMMYYYY(data.date);

                    setFormData((prev: any) => {
                        const updatedFormData = {
                            ...prev,
                            // Set matter data for timeSlip
                            matterts: matterData ? matterData.label : null,
                            // Mark that the TimeSlip matter is set from an existing TimeSlip
                            // and should not be changed by communication matter changes
                            matterts_manually_set: true,

                            // Set timeSlip specific fields
                            duration: data.duration || "",
                            description: data.description || "", // Use description instead of descriptionts
                            note: data.notes || "", // Notes field - use "note" not "notets"
                            billable: data.billable !== undefined ? data.billable : true,
                            timekeeper: data.timekeeper || "",

                            // Set Activity Code (from "type" field) - store as string
                            activityCode: activityCodeData,

                            // Set Task Code (from "project" field) - store as string
                            taskCode: taskCodeData,

                            // Set date in DD-MM-YYYY format
                            datets: formattedDate,

                            isTimeSlipReadOnly: prev.isTimeSlipReadOnly || true,
                            createTimeslip: false// Disable timeslip creation since it's already logged
                        };
                        return updatedFormData;
                    });
                }


                return data;
                //}
            } else {
                console.error("TimeSlip Detail API failed:", response.status, response.statusText);
                return null;
            }
        } catch (error) {
            console.error("Error calling TimeSlip Detail API:", error);
            return null;
        }
    };

    // Function to get ClarraDocs details by ID
    let ClarraDocsAbortController: AbortController | null = null;
    const getClarraDocsDetailId = async (clarraDocsId: string, ItemId: string) => {
        try {
            const token = await getAccessToken();
            if (!token) {
                console.error("No auth token found for ClarraDocs detail API");
                return null;
            }

            const API_BASE_URL1 = getStorageItem(LOCALSTORAGE_KEYS.apibaseurl) || API_BASE_URL;
            const apiUrl = `${API_BASE_URL1}ClarraDocs/GetClarraDoc/${clarraDocsId}`;

            if (ClarraDocsAbortController) {
                ClarraDocsAbortController.abort();
                console.log("Abort Previous request");
            }
            ClarraDocsAbortController = new AbortController();
            // console.log("Calling ClarraDocs Detail API:", apiUrl);

            const response = await fetch(apiUrl, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                },
                signal: ClarraDocsAbortController.signal
            });

            if (response.ok) {
                const data = await response.json();
                if (data) {
                    let matterData = null;
                    if (data.matterNumber) {
                        // Find the matter option that contains the matterNumber in its label
                        const matchingMatter = mattersoptions.find(option => {
                            const match = option.label.match(/\((\d+)\)$/); // Format: "Matter Name (Number)"
                            return match && match[1] === data.matterNumber;
                        });

                        if (matchingMatter) {
                            matterData = matchingMatter;
                            //  console.log("Matched: Found matching matter:", matterData);
                        } else {
                            matterData = {
                                value: data.matterNumber,
                                label: data.matterNumber
                            };
                        }

                    }


                    // Convert date from "Friday, 01 August 2025" to DD-MM-YYYY format
                    const formatDateToDDMMYYYY = (dateString) => {
                        if (!dateString) return null;
                        try {
                            // Parse the date string (assuming "Day, DD Month YYYY" format from API)
                            const dateParts = dateString.split(', ')[1].split(' ');
                            const day = dateParts[0].padStart(2, '0');

                            // Convert month name to month number
                            const months = {
                                'January': '01', 'February': '02', 'March': '03', 'April': '04',
                                'May': '05', 'June': '06', 'July': '07', 'August': '08',
                                'September': '09', 'October': '10', 'November': '11', 'December': '12'
                            };
                            const month = months[dateParts[1]];
                            const year = dateParts[2];

                            return `${day}-${month}-${year}`;
                        } catch (error) {
                            console.error("Error formatting date:", error);
                            return null;
                        }
                    };

                    const formattedDate = formatDateToDDMMYYYY(data.date);

                    const formattedTags = data.tags ? data.tags.map(tag => ({
                        label: tag,
                        value: tag
                    })) : [];

                    // Update form data with ClarraDocs details
                    setFormData((prev: any) => {
                        const updatedFormData = {
                            ...prev,
                            // Set matter data for ClarraDocs
                            matterdoc: matterData ? matterData.label : null,
                            clarraDocsMatterNumber: data.matterNumber,
                            // Mark that the ClarraDocs matter is set from an existing ClarraDocs
                            // and should not be changed by communication matter changes
                            matterdoc_manually_set: true,

                            // Set ClarraDocs specific fields
                            datedoc: formattedDate,
                            // Format status to match the expected format in LogClarraDocsForm
                            status: { label: data.status },
                            serviceType: { label: data.mailType },
                            tagdoc: formattedTags,
                            descriptiondoc: data.description,
                            notedoc: data.note,
                            sendToDocketing: data.sendToDocketing,
                            docketingComplete: data.completeDocketing,
                            // sendToDocketing: Boolean(data.sendToDocketing),
                            // docketingComplete: Boolean(data.completeDocketing),

                            docketingNotes: data.docketingNote,

                            // Mark as read-only since it's already logged
                            //isClarraDocsReadOnly: true,
                            isClarraDocsReadOnly: prev.isClarraDocsReadOnly || true,
                            createClarraDocs: false // Disable ClarraDocs creation since it's already logged
                        };
                        
                        // Please do not remove that

                        // console.log("Updated formData with ClarraDocs details:", {
                        //     matterdoc: updatedFormData.matterdoc,
                        //     datedoc: updatedFormData.datedoc,
                        //     status: updatedFormData.status,
                        //     serviceType: updatedFormData.serviceType,
                        //     tagdoc: updatedFormData.tagdoc,
                        //     descriptiondoc: updatedFormData.descriptiondoc,
                        //     notedoc: updatedFormData.notedoc,
                        //     sendToDocketing: updatedFormData.sendToDocketing,
                        //     docketingComplete: updatedFormData.docketingComplete,
                        //     docketingNotes: updatedFormData.docketingNotes,
                        //     isClarraDocsReadOnly: updatedFormData.isClarraDocsReadOnly,
                        //     createClarraDocs: updatedFormData.createClarraDocs
                        // });

                        return updatedFormData;
                    });
                }

                return data;
                // }
            } else {
                console.error("ClarraDocs Detail API failed:", response.status, response.statusText);
                return null;
            }
        } catch (error) {
            console.error("Error calling ClarraDocs Detail API:", error);
            return null;
        }
    };

    function parseFullNameWithEmail(s) {
        if (!s) return { displayName: "", email: "" };

        // API sends: "Name (email@domain)"
        const m = s.match(/^(.*)\s\(([^)]+)\)\s*$/);
        if (!m) {
            // Fallback: try already formatted "Name <email@domain>"
            const m2 = s.match(/^(.*)\s<([^>]+)>\s*$/);
            if (m2) {
                return {
                    displayName: (m2[1] || "").trim() || (m2[2] || "").trim(),
                    email: (m2[2] || "").trim(),
                };
            }
            // Last resort: treat the whole string as email
            return { displayName: s, email: s };
        }

        const displayName = (m[1] || "").trim();
        const email = (m[2] || "").trim();
        return { displayName: displayName || email, email };
    }


    function toDropdownOptionFromPerson(p) {
        const { displayName, email } = parseFullNameWithEmail(p?.fullNameWithEmail);
        const contactID = p?.contactID ?? 0;
        const userID = p?.userID ?? "";
        return {
            label: `${displayName || email} <${email}>`,
            value: `${contactID}|${userID}|${email}`,
        };
    }

    function dedupeByEmail(options) {
        const seen = new Set();
        return (options || []).filter(opt => {
            const m = typeof opt.label === "string" ? opt.label.match(/<([^>]+)>/) : null;
            const email = m ? m[1].toLowerCase() : (opt.value?.split("|")[2] || "").toLowerCase();
            if (!email) return false;
            if (seen.has(email)) return false;
            seen.add(email);
            return true;
        });
    }


    const [accessToken, setAccessToken] = useState<string | null>(null);
    // const [errors, setErrors] = useState<any>({});
    const [isEmailLogged, setEmailLogged] = useState(false);
    const [isEmailRead, setIsEmailRead] = useState(false);
    const [IsLoader, setIsLoader] = useState(false);
    const [HideView, SetHideView] = useState(false);
    const [EmailItemId, SetEmailItemId] = useState(null);
    const [PreviousEmailItemId, SetPreviousEmailItemId] = useState(null);
    let rl = false;
    useEffect(() => {
        return;
        (async () => {
            if (Reset == true) {
                SetIsReset(true);
                // let token = getAccessToken(); 
                SetStorageItem("state", "1");
                if (isEmailRead == false) {
                    setIsEmailRead(true);
                    if (!rl) {
                        rl = true;
                        Office.onReady(() => {
                            const item = Office.context.mailbox.item;
                            if (item) {
                                console.log("Current email itemId:", item.itemId);
                                console.log("Current email itemId:", item.internetMessageId);
                                ReadEmailItem(0, item).then((itemId) => {
                                    // console.log("ItemId on start", itemId);
                                });
                            } else {
                                console.warn("No email item found — likely invalid context.");
                            }
                        });
                    }
                }
            }
        })();
    }, []);
    
    const formatTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
    const prevRecipientCount = useRef(0);
    const ignoreNextRecipientsChanged = useRef(false);

    const prevCcRecipientCount = useRef(0);
    const ignoreNextCcRecipientsChanged = useRef(false);
    const ccFormatTimeout = useRef(null);

    // Supporting state for the recipient-sync polling fallback (see the live-sync effect below).
    // lastToSig/lastCcSig hold a signature of the recipient emails last pushed into the form, so a
    // poll only touches React state when the Outlook field actually changed.
    // reformatInFlight is true while we clear-then-reset a field ourselves, so a poll landing
    // mid-reformat can't read the momentarily-empty field and wipe the form.
    const lastToSig = useRef<string | null>(null);
    const lastCcSig = useRef<string | null>(null);
    const reformatInFlight = useRef(false);

    // Emails only, lowercased and sorted - display-name churn (which our own reformat causes)
    // must not read as a recipient change, or the poll would loop against itself.
    const sigOfRecipients = (list: any[]) =>
        (list || []).map(r => (r?.emailAddress || "").trim().toLowerCase()).sort().join(",");

    useEffect(() => {
        if (!isComposeMode || !contacts.length) return;

        const getAndSetRecipients = (source = "unknown") => {
            if (ignoreNextRecipientsChanged.current) {
                ignoreNextRecipientsChanged.current = false;
                return;
            }
            if (!Office.context.mailbox.item.to || !Office.context.mailbox.item.to.getAsync) {
                return;
            }
            Office.context.mailbox.item.to.getAsync((result) => {
                if (result.status !== Office.AsyncResultStatus.Succeeded) {
                    return;
                }
                const outlookRecipients = result.value || [];
                const dropdownList = outlookRecipients.map(r => {
                    const found = emailOptions.find(opt => {
                        const m = opt.label.match(/<([^>]+)>/);
                        return m && m[1].trim().toLowerCase() === r.emailAddress.toLowerCase();
                    });
                    if (found) {
                        return { label: found.label, value: r.emailAddress };
                    } else if (r.displayName && r.displayName !== r.emailAddress) {
                        return { label: `${r.displayName} <${r.emailAddress}>`, value: r.emailAddress };
                    } else {
                        return { label: `${r.emailAddress} <${r.emailAddress}>`, value: r.emailAddress };
                    }
                });
                setSelectedTo(dropdownList);
                setFormData(prev => ({ ...prev, recipents: dropdownList }));
                lastToSig.current = sigOfRecipients(outlookRecipients); // keep the poll baseline current
                SetStorageItem(
                   LOCALSTORAGE_KEYS.SelectedTO,
                    dropdownList.length > 0 ? dropdownList.map(opt => opt.label).join(", ") : ""
                );

                if (outlookRecipients.length > prevRecipientCount.current) {
                    if (formatTimeout.current) clearTimeout(formatTimeout.current);
                    formatTimeout.current = setTimeout(() => {
                        const formatted = outlookRecipients.map(r => {
                            const email = r.emailAddress;
                            const found = emailOptions.find(eo => {
                                const eoMatch = eo.label.match(/<([^>]+)>/);
                                return eoMatch && eoMatch[1].trim().toLowerCase() === email.toLowerCase();
                            });
                            if (found) {
                                const idx = found.label.indexOf(' <');
                                const displayName = idx > -1 ? found.label.slice(0, idx) : found.label;
                                return { emailAddress: email, displayName };
                            } else {
                                if (r.displayName && r.displayName !== r.emailAddress) {
                                    return { emailAddress: email, displayName: r.displayName };
                                } else {
                                    return { emailAddress: email, displayName: '' };
                                }
                            }
                        });
                        ignoreNextRecipientsChanged.current = true;
                        reformatInFlight.current = true; // block the poll until both setAsync calls land
                        Office.context.mailbox.item.to.setAsync([], () => {
                            Office.context.mailbox.item.to.setAsync(formatted, () => {
                                lastToSig.current = sigOfRecipients(formatted);
                                reformatInFlight.current = false;
                            });
                        });
                    }, 300);
                }
                prevRecipientCount.current = outlookRecipients.length;
            });
        };

        const getAndSetCcRecipients = (source = "unknown") => {
            if (ignoreNextCcRecipientsChanged.current) {
                ignoreNextCcRecipientsChanged.current = false;
                return;
            }
            if (!Office.context.mailbox.item.cc || !Office.context.mailbox.item.cc.getAsync) {
                return;
            }
            Office.context.mailbox.item.cc.getAsync((result) => {
                if (result.status !== Office.AsyncResultStatus.Succeeded) {
                    return;
                }
                const ccRecipients = result.value || [];
                const dropdownList = ccRecipients.map(r => {
                    const found = emailOptions.find(opt => {
                        const m = opt.label.match(/<([^>]+)>/);
                        return m && m[1].trim().toLowerCase() === r.emailAddress.toLowerCase();
                    });
                    if (found) {
                        return { label: found.label, value: r.emailAddress };
                    } else if (r.displayName && r.displayName !== r.emailAddress) {
                        return { label: `${r.displayName} <${r.emailAddress}>`, value: r.emailAddress };
                    } else {
                        return { label: `${r.emailAddress} <${r.emailAddress}>`, value: r.emailAddress };
                    }
                });
                setSelectedCc(dropdownList);
                setFormData(prev => ({ ...prev, cc: dropdownList }));
                lastCcSig.current = sigOfRecipients(ccRecipients); // keep the poll baseline current
                SetStorageItem(
                    LOCALSTORAGE_KEYS.SelectedCC,
                    dropdownList.length > 0 ? dropdownList.map(opt => opt.label).join(", ") : ""
                );

                if (ccRecipients.length > (prevCcRecipientCount.current || 0)) {
                    if (ccFormatTimeout.current) clearTimeout(ccFormatTimeout.current);
                    ccFormatTimeout.current = setTimeout(() => {
                        const formatted = ccRecipients.map(r => {
                            const email = r.emailAddress;
                            const found = emailOptions.find(eo => {
                                const eoMatch = eo.label.match(/<([^>]+)>/);
                                return eoMatch && eoMatch[1].trim().toLowerCase() === email.toLowerCase();
                            });
                            if (found) {
                                const idx = found.label.indexOf(' <');
                                const displayName = idx > -1 ? found.label.slice(0, idx) : found.label;
                                return { emailAddress: email, displayName };
                            } else {
                                if (r.displayName && r.displayName !== r.emailAddress) {
                                    return { emailAddress: email, displayName: r.displayName };
                                } else {
                                    return { emailAddress: email, displayName: '' };
                                }
                            }
                        });
                        ignoreNextCcRecipientsChanged.current = true;
                        reformatInFlight.current = true; // block the poll until both setAsync calls land
                        Office.context.mailbox.item.cc.setAsync([], () => {
                            Office.context.mailbox.item.cc.setAsync(formatted, () => {
                                lastCcSig.current = sigOfRecipients(formatted);
                                reformatInFlight.current = false;
                            });
                        });
                    }, 600);
                }
                prevCcRecipientCount.current = ccRecipients.length;
            });
        };

        // Two callers: our own manual call just below (eventArg undefined) to read the initial
        // state, and Outlook's event dispatch (eventArg is an OlkRecipientsChangedEventArgs).
        // The polling fallback does NOT come through here - it calls getAndSetRecipients /
        // getAndSetCcRecipients directly with source "POLL".
        const unifiedRecipientsChangedHandler = (eventArg?: any) => {
            const source = eventArg ? "OUTLOOK-EVENT" : "MOUNT";
            getAndSetRecipients(source);
            getAndSetCcRecipients(source);
        };

        unifiedRecipientsChangedHandler();

        Office.context.mailbox.item.addHandlerAsync(
            Office.EventType.RecipientsChanged,
            unifiedRecipientsChangedHandler,
            (regResult) => {
                // A failure here means Outlook will never call the handler, and on platforms
                // without the poll below the To/Cc fields would silently stop syncing.
                if (regResult.status !== Office.AsyncResultStatus.Succeeded) {
                    console.warn("[Clarra] RecipientsChanged registration failed:", regResult.error);
                }
            }
        );

        // ---- POLLING FALLBACK ----
        // On Outlook on the web and new Outlook, addHandlerAsync(RecipientsChanged) reports
        // 'succeeded' but the handler is never invoked, so edits made in Outlook's own To/Cc
        // fields never reach this add-in. (Verified on OutlookWebApp: registration succeeded,
        // Mailbox 1.7 supported, and it is not the known ItemChanged interference - disabling
        // ItemChanged changed nothing.) to.getAsync/cc.getAsync work fine there, so poll them.
        // Classic Outlook delivers the event correctly and stays purely event-driven.
        const platform = Office.context.platform;
        const needsPolling =
            platform === Office.PlatformType.OfficeOnline ||
            platform === Office.PlatformType.Universal;

        let pollTimer: ReturnType<typeof setInterval> | null = null;

        if (needsPolling) {
            const pollRecipientFields = () => {
                if (reformatInFlight.current) return; // field is momentarily half-written by us
                const pollItem = Office.context.mailbox.item as any;
                if (!pollItem) return;

                if (pollItem.to && typeof pollItem.to.getAsync === 'function') {
                    pollItem.to.getAsync((toRes: any) => {
                        if (toRes.status !== Office.AsyncResultStatus.Succeeded) return;
                        const sig = sigOfRecipients(toRes.value);
                        if (sig === lastToSig.current) return; // nothing changed, stay quiet
                        console.log("[Clarra] TO field changed in Outlook, syncing to form:", sig);
                        lastToSig.current = sig;
                        // Our own reformat sets this flag expecting the event to consume it. The
                        // event never fires here, so it would go stale and swallow a real change.
                        ignoreNextRecipientsChanged.current = false;
                        getAndSetRecipients("POLL");
                    });
                }

                if (pollItem.cc && typeof pollItem.cc.getAsync === 'function') {
                    pollItem.cc.getAsync((ccRes: any) => {
                        if (ccRes.status !== Office.AsyncResultStatus.Succeeded) return;
                        const sig = sigOfRecipients(ccRes.value);
                        if (sig === lastCcSig.current) return;
                        console.log("[Clarra] CC field changed in Outlook, syncing to form:", sig);
                        lastCcSig.current = sig;
                        ignoreNextCcRecipientsChanged.current = false;
                        getAndSetCcRecipients("POLL");
                    });
                }
            };

            console.log("[Clarra] Recipient sync: polling (RecipientsChanged is unreliable on this platform) | platform:", platform);
            pollTimer = setInterval(pollRecipientFields, 1000);
        } else {
            console.log("[Clarra] Recipient sync: event-driven | platform:", platform);
        }

        return () => {
            Office.context.mailbox.item.removeHandlerAsync(
                Office.EventType.RecipientsChanged,
                unifiedRecipientsChangedHandler
            );
            if (pollTimer) clearInterval(pollTimer);
            if (formatTimeout.current) clearTimeout(formatTimeout.current);
            if (ccFormatTimeout.current) clearTimeout(ccFormatTimeout.current);
        };
    }, [isComposeMode, contacts]);
    

    useEffect(() => {
        SetHideView(hideView);
    }, [hideView]);
  
    const setToRecipients = (Emailobject: any) => {
        if (Office.context.mailbox.item) {
            // Use the API to set recipients in the "To" field
            const recipients = Emailobject;
           
            Office.context.mailbox.item.to.setAsync(
                recipients,
                (result) => {
                    if (result.status === Office.AsyncResultStatus.Succeeded) {
                        //console.log('To recipients set successfully.');
                    } else {
                        console.error('Failed to set To recipients:', result.error);
                    }
                }
            );
        } else {
            console.error('No mailbox item available.');
        }
    };

    const RemoveRecipients = (Emailobject: any) => {
        if (Office.context.mailbox.item) {
            // Use the API to set recipients in the "To" field

            const recipients = [
                { emailAddress: Emailobject, displayName: Emailobject }
                
            ];

            Office.context.mailbox.item.to.setAsync(
                recipients,
                (result) => {
                    if (result.status === Office.AsyncResultStatus.Succeeded) {
                        // console.log('To recipients set successfully.');
                    } else {
                        console.error('Failed to set To recipients:', result.error);
                    }
                }
            );
        } else {
            console.error('No mailbox item available.');
        }
    };

    const validateForm = (mode: any) => {
        if (!mode.cm) {
            return true;
        }
        let tempErrors: any = {};
        if (selectedMatter == null) {
            tempErrors.matter = `Please select a ${uiDictionary.Matter}`;
        }

        // if (selectedTags.length === 0) {
        //   tempErrors.tags = "At least one tag is required";
        // }
        if (!selectedFrom || !selectedFrom) {
            tempErrors.from = "Please select a sender";
        }
        if (selectedTo.length === 0) {
            tempErrors.to = "At least one recipient is required";
        }

        setErrors(tempErrors);
        return Object.keys(tempErrors).length === 0;
    };

    const Tag = ({ text, onRemove }) => (
        <span className="inline-flex items-center bg-blue-50 rounded-md px-2 py-1 m-1">
            <span className="text-sm text-gray-700">{text}</span>
            <button
                onClick={onRemove}
                className="ml-1 text-gray-400 hover:text-gray-600 focus:outline-none"
            >
                <X size={14} />
            </button>
        </span>
    );



    return (
        <div className="max-w-2xl bg-white">
            {IsLoader && (
                <Box
                    sx={{
                        position: "fixed",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        zIndex: 1300,
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        backgroundColor: "rgba(255, 255, 255, 1)",
                    }}
                >
                    <Loader />
                </Box>
            )}
            {false && (
                <div
                    style={{
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        alignItems: "center",
                        height: "90vh", // Ensure it takes the full viewport height
                        textAlign: "center",
                        margin: "0 auto",
                    }}
                >
                    <div>
                        <img
                            src="../../assets/success.jpg"
                            alt="Success"
                            style={{
                                width: "100px",
                                height: "100px",
                                marginBottom: "2px", // Add spacing between the image and text
                            }}
                        />
                    </div>
                    <h2
                        style={{
                            fontSize: "18px",
                            fontWeight: "bold",
                            color: "green",
                        }}
                    >
                        The email has been successfully logged.
                    </h2>
                </div>
            )}
            {!HideView && (
                <div>
                    
                    <FormControl fullWidth>
                        <Dropdown
                            label="From"
                            value={selectedFrom}
                            options={emailOptions}
                            onChange={() => { }}
                            isMulti={false}
                            required
                            disabled={true}
                        />
                        {expanded.includes('panel1') && formData.createCommunication && !selectedFrom && errors.From && (
                            <p className="text-red-500 text-xs">{errors.From}</p>
                        )}

                    </FormControl>


                    <FormControl fullWidth>
                        <Dropdown
                            label="To"
                            value={selectedTo}
                            options={emailOptions}
                            onChange={handleToChange}
                            isMulti={true}
                            required={false}
                            disabled={formData.isCEmailLogged}
                            isLoading={contactsLoading}
                            loadingLabel={`Loading contacts (${contacts.length.toLocaleString()})...`}
                        />
                        {expanded.includes('panel1') && formData.createCommunication && !selectedTo.length && errors.To && (
                            <p className="text-red-500 text-xs">{errors.To}</p>
                        )}
                    </FormControl>

                    <FormControl fullWidth>
                        <Dropdown
                            label="Cc"
                            value={selectedCc}
                            options={emailOptions}
                            onChange={handleCcChange}
                            isMulti={true}
                            required={false}
                            disabled={formData.isCEmailLogged || (!isComposeMode && isCcDisabled)}
                            isLoading={contactsLoading}
                            loadingLabel={`Loading contacts (${contacts.length.toLocaleString()})...`}
                        />
                        {expanded.includes('panel1') && formData.createCommunication && !selectedCc.length && errors.Cc && (
                            <p className="text-red-500 text-xs">{errors.Cc}</p>
                        )}
                    </FormControl>

                    <FormControl fullWidth sx={{ marginTop: '10px' }}>
                        <Dropdown
                            // label="Matter"
                             label={uiDictionary.Matter}
                            value={selectedMatter}
                            options={mattersoptions}
                            // onChange={setSelectedMatter}
                            onChange={handleMatterChange}
                            isMulti={false}
                            required={true}
                            disabled={formData.isCEmailLogged}
                            isLoading={mattersLoading}
                            loadingLabel={`Loading ${uiDictionary.Matter} (${matters.length.toLocaleString()})...`}
                        />
                       
                        {expanded.includes('panel1') && formData.createCommunication && !formData.matter && errors.matter && (
                            <p className="text-red-500 text-xs">{errors.matter}</p>
                        )}
                    </FormControl>
                    <FormControl fullWidth>
                        <Dropdown
                            label={uiDictionary.Tags}
                            value={selectedTags}
                            options={sampleTags}
                            // onChange={setSelectedTags}
                            onChange={handleTagsChange}
                            isMulti={true}
                            disabled={formData.isCEmailLogged}
                        />
                        {expanded.includes('panel1') && formData.createCommunication && formData.tag.length == 0 && errors.tag && (
                            <p className="text-red-500 text-xs">{errors.tag}</p>
                        )}
                    </FormControl>
                    
                </div>
            )}
        </div>
    );
};

export default EmailLoggingForm;
